import React, { useMemo, useState, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { getConfig } from '../runtimeConfig';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  ComposedChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface Stats {
  TOTAL_BINS: number;
  TOTAL_WAREHOUSES: number;
  TOTAL_SHELF_SIZES: number;
  TOTAL_STORAGE_AREAS: number;
  TOTAL_SEGMENTS: number;
  FREE_SEGMENTS: number;
  USED_SEGMENTS: number;
  FREE_PCT: number;
  UTILIZED_PCT: number;
  AVG_LEVELS: number;
  MAX_LEVELS: number;
  MIN_LEVELS: number;
}

interface VirtualStats {
  TOTAL_BINS: number;
  TOTAL_WAREHOUSES: number;
  TOTAL_SHELF_SIZES: number;
  TOTAL_STORAGE_AREAS: number;
  TOTAL_SEGMENTS: number;
  FREE_SEGMENTS: number;
  USED_SEGMENTS: number;
  FREE_PCT: number;
  UTILIZED_PCT: number;
  AVG_LEVELS: number;
  MAX_LEVELS: number;
  MIN_LEVELS: number;
}

interface CustomerStorageStats {
  TOTAL_CUSTOMERS: number;
  PHYSICAL_BIN_CAPACITY: number;
  BIN_USED_PALLETS: number;
  VIRTUAL_USED_PALLETS: number;
  FLOOR_USED_PALLETS: number;
  TOTAL_USED_PALLETS: number;
  BIN_FREE_CAPACITY: number;
  BIN_UTILIZATION_PCT: number;
  FREE_PCT: number;
  TEMP_BLOCKED_PALLETS: number;
  TEMP_BLOCKED_BINS: number;
  MANUAL_CUSTOMERS: number;
  MANUAL_PALLETS: number;
  MANUAL_CAPACITY: number;
  TOTAL_CAPACITY_WITH_MANUAL: number;
  AREA_CUSTOMER_BLOCKED_BINS: number;
  AREA_CUSTOMER_BLOCKED_PALLETS: number;
  AREA_CUSTOMER_FULL_BLOCKED: number;
}

interface ExecutiveDashboardProps {
  stats: Stats | null;
  customerStorageStats: CustomerStorageStats | null;
  customerStorageData: any[];
  warehouseData: any[];
  shelfData: any[];
  areaData: any[];
  rawWarehouseData: any[];
  virtualStats: VirtualStats | null;
  virtualWarehouseData: any[];
  virtualAreaData: any[];
  manualEntries: any[];
  areaCustomerBlockedData: any[];
  loading: boolean;
  selectedWarehouses: string[];
  filters: any;
  onRefresh: () => void;
  onResetFilters: () => void;
  onOpenAI: () => void;
  formatNumber: (n: number) => string;
  getWarehouseDisplayName: (code: string) => string;
  uomData?: { summary: any; data: any[] };
  onNavigate?: (page: string) => void;
  userWarehouse?: string | null;
  isAdmin?: boolean;
}

const CHART_COLORS = ['#6366f1', '#ff9100', '#9c27b0', '#ff3b30', '#34c759', '#00b4d8', '#fbbf24', '#ec4899'];
const WAREHOUSE_COLOR_MAP: Record<string, string> = {
  DPW: '#6366f1',
  DSI: '#00b4d8',
  JDA: '#34c759',
  JDB: '#ff9100',
  JUB: '#ff3b30',
  RUH: '#9c27b0',
};

const ExecutiveDashboard: React.FC<ExecutiveDashboardProps> = ({
  stats,
  customerStorageStats,
  customerStorageData,
  warehouseData,
  shelfData,
  areaData,
  rawWarehouseData,
  virtualStats,
  virtualWarehouseData,
  virtualAreaData,
  manualEntries,
  areaCustomerBlockedData,
  loading,
  selectedWarehouses,
  filters,
  onRefresh,
  onResetFilters,
  onOpenAI,
  formatNumber,
  getWarehouseDisplayName,
  uomData,
  onNavigate,
  userWarehouse,
  isAdmin: isAdminProp,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'customers' | 'warehouses' | 'analytics' | 'dynamic'>('overview');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSort, setCustomerSort] = useState<{ field: string; asc: boolean }>({ field: 'CUSTOMER', asc: true });
  const [customerPage, setCustomerPage] = useState(0);
  const [storageTypeFilter, setStorageTypeFilter] = useState<string>('All');
  // Auto-set warehouse for non-admin users (warehouse_supervisor sees only their warehouse)
  const [dashboardWarehouse, setDashboardWarehouse] = useState<string>(
    (!isAdminProp && userWarehouse) ? userWarehouse : 'All'
  );
  const [drillDownWh, setDrillDownWh] = useState<string | null>(null);

  // Operations Console filters
  const [ocChamber, setOcChamber] = useState<string>('All');
  const [ocPalletType, setOcPalletType] = useState<string>('All');
  const [ocClient, setOcClient] = useState<string>('All');
  const [ocExcludeBlocked, setOcExcludeBlocked] = useState<boolean>(false);
  const [ocExcludeFixedSpace, setOcExcludeFixedSpace] = useState<boolean>(false);
  // Drill-down panel state for sales dashboard
  const [drillOpen, setDrillOpen] = useState<string | null>(null); // 'warehouse' | 'chamber' | 'client' | 'pallet' | 'filters' | null

  // Customer Minimum Commitments — uses /api/customer-contracts on port 3003
  const API_BASE = getConfig().REACT_APP_API;
  interface CustMinRow {
    id?: number; customer: string; warehouse: string;
    commitmentType: string; commitmentValue: number; conversionFactor: number; minPallets: number;
    storageType: string; notes: string;
  }
  // Conversion factors: value × factor = pallets
  // SQM: 1 ISO pallet = 1.2 SQM → factor = 1/1.2 = 0.8333 pallets per SQM
  // SQFT: 1 SQM = 10.764 SQFT → 1 pallet = 1.2 × 10.764 = 12.917 SQFT → factor = 1/12.917 = 0.07742
  const COMMITMENT_TYPES = [
    { value: 'PP', label: 'Pallet Positions (PP)', unit: 'PP', defaultFactor: 1, displayFactor: 'x1', hint: '' },
    { value: 'SQM', label: 'Square Meters (SQM)', unit: 'SQM', defaultFactor: 0.8333, displayFactor: '÷1.2', hint: '1 pallet = 1.2 SQM (ISO). Pallets = SQM ÷ 1.2' },
    { value: 'SQFT', label: 'Square Feet (SQFT)', unit: 'SQFT', defaultFactor: 0.07742, displayFactor: '÷12.917', hint: '1 pallet = 12.917 SQFT. Pallets = SQFT ÷ 12.917' },
    { value: 'BINS', label: 'Bin Locations', unit: 'Bins', defaultFactor: 1, displayFactor: 'x1', hint: '' },
    { value: 'MT', label: 'Metric Tons (MT)', unit: 'MT', defaultFactor: 0.714, displayFactor: 'x0.714', hint: '1 MT ≈ 0.714 pallets' },
    { value: 'KT', label: 'Kilo Tons (KT)', unit: 'KT', defaultFactor: 714.29, displayFactor: 'x714.29', hint: '1 KT = 714.29 pallets' },
    { value: 'CBM', label: 'Cubic Meters (CBM)', unit: 'CBM', defaultFactor: 0.5, displayFactor: 'x0.5', hint: '1 CBM = 0.5 pallets' },
  ];
  const STORAGE_TYPES = ['Ambient', 'Temperature Controlled', 'Cold Storage', 'DG (Dangerous Goods)', 'Bulk', 'Bagged'];
  const [custMinRows, setCustMinRows] = useState<CustMinRow[]>([]);
  const [custMinLoading, setCustMinLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchCustMin = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/customer-contracts?active=true`);
      const json = await res.json();
      if (json.success && json.data) {
        setCustMinRows(json.data.map((r: any) => {
          const cType = (r.storage_type || 'PP').toUpperCase();
          const ct = COMMITMENT_TYPES.find(t => t.value === cType);
          const factor = ct ? ct.defaultFactor : 1;
          const minVal = r.minimum_value || 0;
          return {
            id: r.id, customer: r.customer_name || '', warehouse: r.facility || 'All',
            commitmentType: cType, commitmentValue: minVal,
            conversionFactor: factor,
            minPallets: Math.ceil(minVal * factor),
            storageType: cType === 'PP' ? 'Ambient' : cType,
            notes: r.notes || '',
          };
        }));
      }
    } catch (err) { console.error('Failed to load customer contracts:', err); }
  }, [API_BASE]);

  // Latest closing balance per customer from WeeklyClientEntries (last activity)
  const [custLastBalance, setCustLastBalance] = useState<Record<string, number>>({});
  // Full latest-week entries for building MANUAL customer records (with capacity, area, etc.)
  const [weeklyLatestEntries, setWeeklyLatestEntries] = useState<any[]>([]);

  const fetchCustLastBalance = useCallback(async () => {
    try {
      const currentYear = new Date().getFullYear();
      // Fetch current year + previous year to find latest activity
      const [resCur, resPrev] = await Promise.all([
        fetch(`${API_BASE}/weekly-entries?year=${currentYear}`).then(r => r.json()).catch(() => ({ data: [] })),
        fetch(`${API_BASE}/weekly-entries?year=${currentYear - 1}`).then(r => r.json()).catch(() => ({ data: [] })),
      ]);
      const allEntries = [...(resCur.data || []), ...(resPrev.data || [])];

      // For each customer+warehouse+area, find the LATEST week entry (full record)
      const latestByKey: Record<string, any> = {};

      allEntries.forEach((e: any) => {
        const cust = (e.client_name || '').trim();
        const wh = (e.warehouse || '').trim();
        const area = (e.warehouse_area || '').trim();
        const yr = e.year || 0;
        const wk = e.week_number || 0;
        if (!cust) return;

        // Key: customer + warehouse + area (most granular)
        const key = area ? `${cust}__${wh}__${area}` : `${cust}__${wh}`;
        const prev = latestByKey[key];
        if (!prev || yr > prev.year || (yr === prev.year && wk > prev.week_number)) {
          latestByKey[key] = { ...e, _key: key };
        }
      });

      // Build balance map (for customer minimum commitments)
      const result: Record<string, number> = {};
      // Also collect the latest entries for building MANUAL customer storage records
      const latestEntries: any[] = [];

      // Separate area-level vs warehouse-level keys
      const areaKeys = new Set<string>(); // "CUST__WH" that have area-level entries
      Object.keys(latestByKey).forEach(key => {
        const parts = key.split('__');
        if (parts.length === 3) {
          areaKeys.add(`${parts[0]}__${parts[1]}`);
        }
      });

      Object.entries(latestByKey).forEach(([key, entry]) => {
        const bal = entry.closing_balance || 0;
        const parts = key.split('__');
        const cust = parts[0];
        const wh = parts[1];
        const whKey = `${cust}__${wh}`;
        const isAreaLevel = parts.length === 3;
        const hasAreaEntries = areaKeys.has(whKey);

        // Store the per-key balance
        result[key] = bal;
        latestEntries.push(entry);

        // Build warehouse-level totals: sum area entries, or use warehouse entry if no areas
        if (isAreaLevel) {
          result[whKey] = (result[whKey] || 0) + bal;
        } else if (!hasAreaEntries) {
          // No area entries exist for this customer+warehouse, use warehouse-level directly
          result[whKey] = bal;
        }
        // Skip warehouse-level entry if area entries exist (they'll sum up instead)
      });

      // Build customer-level totals from warehouse-level totals
      const custWhDone = new Set<string>();
      Object.keys(result).forEach(key => {
        const parts = key.split('__');
        if (parts.length === 2 && !key.startsWith('__')) {
          const cust = parts[0];
          const whKey = key;
          if (!custWhDone.has(whKey)) {
            custWhDone.add(whKey);
            result[cust] = (result[cust] || 0) + result[whKey];
          }
        }
      });

      setCustLastBalance(result);
      setWeeklyLatestEntries(latestEntries);
    } catch (err) { console.error('Failed to load weekly balances:', err); }
  }, [API_BASE]);

  useEffect(() => { fetchCustMin(); fetchCustLastBalance(); }, [fetchCustMin, fetchCustLastBalance]);

  const addCustMin = useCallback(async (data: any) => {
    setCustMinLoading(true);
    try {
      const res = await fetch(`${API_BASE}/customer-contracts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: data.customer,
          facility: data.warehouse || 'All',
          storage_type: data.commitment_type || 'PP',
          minimum_value: data.commitment_value,
          minimum_text: `${data.commitment_value} ${data.commitment_type || 'PP'}`,
          notes: data.notes || null,
        }),
      });
      const json = await res.json();
      if (json.success) { await fetchCustMin(); setShowAddForm(false); }
      else alert(json.error || 'Failed to save');
    } catch (err) { alert('Failed to save to server'); }
    setCustMinLoading(false);
  }, [API_BASE, fetchCustMin]);

  const deleteCustMin = useCallback(async (id: number) => {
    if (!window.confirm('Remove this customer commitment?')) return;
    setCustMinLoading(true);
    try {
      const res = await fetch(`${API_BASE}/customer-contracts/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) await fetchCustMin();
    } catch (err) { console.error('Failed to delete:', err); }
    setCustMinLoading(false);
  }, [API_BASE, fetchCustMin]);

  const lastRefreshTime = useMemo(() => new Date().toLocaleTimeString(), []);

  // ✅ FRONTEND FIX: Build correct MANUAL customer data from weekly-entries (latest week only)
  // The backend customer-storage-merged endpoint has 2 bugs:
  //   1. SUMs closing_balance across ALL weeks instead of latest only
  //   2. May not return MANUAL entries at all if the query fails
  // This useMemo: fixes existing MANUAL entries + adds missing MANUAL customers from weekly-entries
  const correctedCustomerStorageData = useMemo(() => {
    // Start with non-MANUAL (IBM) entries from backend — these are correct real-time data
    const ibmEntries = (customerStorageData || []).filter((item: any) => item.STORAGE_TYPE !== 'MANUAL');

    // Build a set of IBM customer+warehouse keys to identify manual-only customers
    const ibmKeys = new Set<string>();
    ibmEntries.forEach((item: any) => {
      const key = `${(item.WAREHOUSE || '').trim().toUpperCase()}_${(item.CUSTOMER || '').trim().toUpperCase()}`;
      ibmKeys.add(key);
    });

    // Build MANUAL entries from weeklyLatestEntries (latest week per customer/warehouse/area)
    // This replaces the broken backend SUM query entirely
    const manualEntryMap = new Map<string, any>();

    weeklyLatestEntries.forEach((entry: any) => {
      const cust = (entry.client_name || '').trim();
      const wh = (entry.warehouse || '').trim();
      const area = (entry.warehouse_area || '').trim() || 'MANUAL';
      const closingBal = entry.closing_balance || 0;
      const areaCap = entry.area_capacity || entry.capacity || 0;

      if (!cust || !wh) return;

      const key = `${wh}_${cust}_${area}`;
      manualEntryMap.set(key, {
        WAREHOUSE: wh,
        CUSTOMER: cust,
        STORAGE_LOCATION: area,
        USED_PALLETS: closingBal,
        TOTAL_BINS: 0,
        TOTAL_CAPACITY: areaCap,
        AVAILABLE_CAPACITY: Math.max(0, areaCap - closingBal),
        UTILIZATION_PCT: areaCap > 0 ? Math.round((closingBal / areaCap) * 100 * 100) / 100 : 0,
        STORAGE_TYPE: 'MANUAL',
        DATA_SOURCE: ibmKeys.has(`${wh.toUpperCase()}_${cust.toUpperCase()}`) ? 'MANUAL-OVERLAP' : 'MANUAL-ONLY',
        UOM: entry.uom || 'PLT',
      });
    });

    // Combine: IBM entries + all MANUAL entries from weekly data
    const manualRecords = Array.from(manualEntryMap.values());
    const combined = [...ibmEntries, ...manualRecords];

    console.log(`📊 Frontend corrected data: ${ibmEntries.length} IBM + ${manualRecords.length} MANUAL = ${combined.length} total records`);

    return combined.sort((a: any, b: any) => {
      if (a.WAREHOUSE !== b.WAREHOUSE) return (a.WAREHOUSE || '').localeCompare(b.WAREHOUSE || '');
      if (a.CUSTOMER !== b.CUSTOMER) return (a.CUSTOMER || '').localeCompare(b.CUSTOMER || '');
      return (a.STORAGE_LOCATION || '').localeCompare(b.STORAGE_LOCATION || '');
    });
  }, [customerStorageData, weeklyLatestEntries]);

  // Build actual pallets per customer+warehouse from ALL data sources (IBM + Manual)
  // This fixes the issue where IBM customers (like SLD with 13K pallets) showed 0
  // because custLastBalance only has WeeklyClientEntries data
  const custActualPallets = useMemo(() => {
    const map: Record<string, number> = {};
    // 1. From correctedCustomerStorageData (IBM + corrected MANUAL merged)
    (correctedCustomerStorageData || []).forEach((item: any) => {
      const cust = (item.CUSTOMER || '').trim();
      const wh = (item.WAREHOUSE || '').trim();
      if (!cust) return;
      const whKey = `${cust}__${wh}`;
      map[whKey] = (map[whKey] || 0) + (item.USED_PALLETS || 0);
      map[cust] = (map[cust] || 0) + (item.USED_PALLETS || 0);
    });
    // 2. Fill in from custLastBalance if missing (weekly entries latest week)
    Object.entries(custLastBalance).forEach(([key, val]) => {
      const parts = key.split('__');
      if (parts.length <= 2 && val > 0 && (!map[key] || map[key] === 0)) {
        map[key] = val;
      }
    });
    return map;
  }, [correctedCustomerStorageData, custLastBalance]);

  // Warehouse-filtered customer data
  const warehouseFilteredCustomerData = useMemo(() => {
    if (dashboardWarehouse === 'All') {
      return correctedCustomerStorageData;
    }
    return correctedCustomerStorageData.filter((item: any) => item.WAREHOUSE === dashboardWarehouse);
  }, [correctedCustomerStorageData, dashboardWarehouse]);

  // UOM data — client-side filtering from warehouseFilteredCustomerData (fixes broken API fetch to port 3003)
  const filteredUomData = useMemo(() => {
    // When "All" → use unfiltered prop directly
    if (dashboardWarehouse === 'All') {
      return uomData || { summary: {}, data: [] };
    }
    // Build UOM breakdown from warehouse-filtered customer storage data (already filtered by dashboardWarehouse)
    const uomMap: Record<string, { UOM: string; PALLET_COUNT: number; TOTAL_QTY: number; RESERVED_QTY: number; ITEM_COUNT: number; AREA_COUNT: number; clients: Set<string> }> = {};
    (warehouseFilteredCustomerData || []).forEach((item: any) => {
      const uom = ((item.UOM || item.STORAGE_TYPE || 'PLT') + '').trim().toUpperCase();
      const pallets = item.USED_PALLETS || item.PALLET_COUNT || 0;
      const totalQty = item.TOTAL_QTY || item.AVAILABLE_QTY || pallets;
      const reservedQty = item.RESERVED_QTY || 0;
      const customer = item.CUSTOMER || item.CLIENT || '';
      const area = item.STORAGE_LOCATION || item.STORAGE_AREA || '';
      if (!uomMap[uom]) {
        uomMap[uom] = { UOM: uom, PALLET_COUNT: 0, TOTAL_QTY: 0, RESERVED_QTY: 0, ITEM_COUNT: 0, AREA_COUNT: 0, clients: new Set() };
      }
      uomMap[uom].PALLET_COUNT += pallets;
      uomMap[uom].TOTAL_QTY += totalQty;
      uomMap[uom].RESERVED_QTY += reservedQty;
      uomMap[uom].ITEM_COUNT += 1;
      if (customer) uomMap[uom].clients.add(customer);
      if (area) uomMap[uom].AREA_COUNT += 1;
    });
    const data = Object.values(uomMap)
      .map(({ clients, ...rest }) => ({ ...rest, CLIENT_COUNT: clients.size }))
      .sort((a, b) => b.PALLET_COUNT - a.PALLET_COUNT);
    const summary = {
      totalPallets: data.reduce((s, d) => s + d.PALLET_COUNT, 0),
      totalQty: data.reduce((s, d) => s + d.TOTAL_QTY, 0),
      uomCount: data.length,
    };
    console.log(`📊 UOM client-side filter for ${dashboardWarehouse}: ${data.length} types, ${summary.totalPallets} pallets`);
    return { summary, data };
  }, [dashboardWarehouse, uomData, warehouseFilteredCustomerData]);

  // Build lookup: which warehouse+location combos have physical bins in PLPSKOP
  // Matches backend SQL: COUNT(*) FROM PLPSKOP WHERE LKASEG != 99 GROUP BY LKKONZ, LKORT
  // If customer STORAGE_LOCATION has 0 physical bins → FLOOR (matches SQL CASE logic)
  const binLocationSet = useMemo(() => {
    const set = new Set<string>();
    rawWarehouseData.forEach(item => {
      if (item.SOURCE === 'MANUAL') return;
      const segs = item.TOTAL_NO_OF_SEGMENTS || 0;
      if (segs === 99) return; // Skip virtual bins (LKASEG=99)
      const wh = (item.WAREHOUSE || '').trim();
      const area = (item.STORAGE_AREA || '').trim();
      // STORAGE_AREA = LKORT-LKBER (e.g. "CHM-01"), extract LKORT prefix
      const lkort = area.includes('-') ? area.split('-')[0].trim() : area;
      if (lkort) set.add(`${wh}__${lkort}`);
    });
    return set;
  }, [rawWarehouseData]);

  // Helper: determine if a customer record is floor/bulk storage
  // Matches SQL: CASE WHEN COALESCE(P.BIN_COUNT, 0) = 0 THEN 'FLOOR' ELSE 'BIN' END
  const isFloorStorage = (item: any): boolean => {
    if (item.STORAGE_TYPE === 'FLOOR' || item.STORAGE_TYPE === 'BULK') return true;
    if (item.STORAGE_TYPE === 'MANUAL') return false;
    const wh = (item.WAREHOUSE || '').trim();
    const loc = (item.STORAGE_LOCATION || '').trim();
    const key = `${wh}__${loc}`;
    return !binLocationSet.has(key);
  };

  // Level distribution analysis
  const levelDistribution = useMemo(() => {
    const map: Record<number, { level: number; bins: number; total_segments: number; used_segments: number }> = {};
    rawWarehouseData.forEach((item) => {
      const lvl = item.NUMBER_OF_LEVELS || 0;
      if (!map[lvl]) map[lvl] = { level: lvl, bins: 0, total_segments: 0, used_segments: 0 };
      map[lvl].bins++;
      map[lvl].total_segments += item.TOTAL_NO_OF_SEGMENTS || 0;
      map[lvl].used_segments += (item.TOTAL_NO_OF_SEGMENTS || 0) - (item.FREE_SEGMENTS || 0);
    });
    return Object.values(map).sort((a, b) => a.level - b.level);
  }, [rawWarehouseData]);

  // Filtered customer data with storage type filter
  const filteredCustomerData = useMemo(() => {
    let data = [...warehouseFilteredCustomerData];

    if (selectedWarehouses.length > 0) {
      data = data.filter((item) => selectedWarehouses.includes(item.WAREHOUSE));
    }

    if (customerSearch.trim()) {
      const search = customerSearch.toLowerCase();
      data = data.filter((item) => item.CUSTOMER?.toLowerCase().includes(search));
    }

    if (storageTypeFilter !== 'All') {
      data = data.filter((item) => {
        if (storageTypeFilter === 'Locations') {
          // Locations = not floor, not manual, not virtual
          return !isFloorStorage(item) && item.STORAGE_TYPE !== 'MANUAL';
        }
        if (storageTypeFilter === 'Virtual') {
          return false;
        }
        if (storageTypeFilter === 'Floor/Bulk') {
          return isFloorStorage(item);
        }
        if (storageTypeFilter === 'Manual') return item.STORAGE_TYPE === 'MANUAL';
        return true;
      });
    }

    return data;
  }, [warehouseFilteredCustomerData, selectedWarehouses, customerSearch, storageTypeFilter]);

  // Sorted customer data
  const sortedCustomerData = useMemo(() => {
    const sorted = [...filteredCustomerData];
    sorted.sort((a, b) => {
      const aVal = a[customerSort.field];
      const bVal = b[customerSort.field];
      const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : Number(aVal) - Number(bVal);
      return customerSort.asc ? cmp : -cmp;
    });
    return sorted;
  }, [filteredCustomerData, customerSort]);

  // Paginated customer data
  const paginatedCustomerData = useMemo(() => {
    const start = customerPage * 50;
    return sortedCustomerData.slice(start, start + 50);
  }, [sortedCustomerData, customerPage]);

  // Warehouse performance data
  const warehousePerformance = useMemo(() => {
    let data = warehouseData || [];
    if (dashboardWarehouse !== 'All') {
      data = data.filter((wh: any) => wh.WAREHOUSE === dashboardWarehouse);
    }
    return data
      .map((wh: any) => ({
        warehouse: wh.WAREHOUSE,
        used_pallets: wh.USED_SEGMENTS || 0,
        free_pallets: wh.FREE_SEGMENTS || 0,
        total_segments: wh.TOTAL_SEGMENTS || 0,
        utilization_pct: wh.UTILIZATION_PCT || 0,
      }))
      .sort((a: any, b: any) => a.warehouse.localeCompare(b.warehouse));
  }, [warehouseData, dashboardWarehouse]);

  // Shelf size distribution — filtered by selected warehouse
  const shelfDistribution = useMemo(() => {
    const map: Record<string, any> = {};
    const filtered = dashboardWarehouse === 'All'
      ? rawWarehouseData
      : rawWarehouseData.filter(item => item.WAREHOUSE === dashboardWarehouse);
    filtered.forEach((item) => {
      const size = item.SHELF_SIZE || 'Unknown';
      if (!map[size]) {
        map[size] = { shelf_size: size, bins: 0, total_segments: 0, used_segments: 0 };
      }
      map[size].bins += 1;
      map[size].total_segments += item.TOTAL_NO_OF_SEGMENTS || 0;
      map[size].used_segments += item.TOTAL_NO_OF_SEGMENTS - (item.FREE_SEGMENTS || 0);
    });

    return Object.values(map)
      .map((item: any, idx: number) => ({
        ...item,
        util_percent: item.total_segments > 0 ? Math.round((item.used_segments / item.total_segments) * 100) : 0,
        fill: CHART_COLORS[idx % CHART_COLORS.length],
      }))
      .sort((a: any, b: any) => b.bins - a.bins);
  }, [rawWarehouseData, dashboardWarehouse]);

  // Area analysis — filtered by selected warehouse
  const areaAnalysis = useMemo(() => {
    const map: Record<string, any> = {};
    const filtered = dashboardWarehouse === 'All'
      ? rawWarehouseData
      : rawWarehouseData.filter(item => item.WAREHOUSE === dashboardWarehouse);
    filtered.forEach((item) => {
      const area = item.STORAGE_AREA || 'Unknown';
      if (!map[area]) {
        map[area] = {
          area: area,
          bins: 0,
          total_segments: 0,
          used_segments: 0,
          levels: [],
        };
      }
      map[area].bins += 1;
      map[area].total_segments += item.TOTAL_NO_OF_SEGMENTS || 0;
      map[area].used_segments += item.TOTAL_NO_OF_SEGMENTS - (item.FREE_SEGMENTS || 0);
      if (item.NUMBER_OF_LEVELS) map[area].levels.push(item.NUMBER_OF_LEVELS);
    });

    return Object.values(map)
      .map((item: any) => ({
        ...item,
        util_percent: item.total_segments > 0 ? Math.round((item.used_segments / item.total_segments) * 100) : 0,
        avg_levels:
          item.levels.length > 0 ? Math.round((item.levels.reduce((a: number, b: number) => a + b) / item.levels.length) * 10) / 10 : 0,
      }))
      .sort((a: any, b: any) => a.area.localeCompare(b.area));
  }, [rawWarehouseData, dashboardWarehouse]);

  // Warehouse cards
  const warehouseCards = useMemo(() => {
    const areaMap: Record<string, Set<string>> = {};
    warehouseFilteredCustomerData.forEach((item) => {
      const wh = item.WAREHOUSE;
      if (!areaMap[wh]) areaMap[wh] = new Set<string>();
      if (item.STORAGE_LOCATION) areaMap[wh].add(item.STORAGE_LOCATION);
    });

    let data = warehouseData || [];
    if (dashboardWarehouse !== 'All') {
      data = data.filter((wh: any) => wh.WAREHOUSE === dashboardWarehouse);
    }

    return data
      .map((wh: any) => ({
        warehouse: wh.WAREHOUSE,
        total_capacity: wh.TOTAL_SEGMENTS || 0,
        used_pallets: wh.USED_SEGMENTS || 0,
        free_pallets: wh.FREE_SEGMENTS || 0,
        area_count: areaMap[wh.WAREHOUSE]?.size || 0,
        util_percent: wh.UTILIZATION_PCT || 0,
      }))
      .sort((a: any, b: any) => a.warehouse.localeCompare(b.warehouse));
  }, [warehouseFilteredCustomerData, warehouseData, dashboardWarehouse]);

  // ALWAYS compute KPIs client-side using cross-reference floor detection
  // The backend customerStorageStats has STORAGE_TYPE='IBM' (not 'FLOOR'/'BIN'),
  // so we must always use the binLocationSet cross-reference to detect floor vs bin
  const computedKpis = useMemo(() => {
    const isAll = dashboardWarehouse === 'All';
    const data = warehouseFilteredCustomerData;
    const uniqueCustomers = new Set(data.map((item) => item.CUSTOMER)).size;

    // Separate BIN vs FLOOR using cross-reference with rawWarehouseData
    let binUsed = 0;
    let floorUsed = 0;
    let manualUsed = 0;

    data.forEach(item => {
      if (item.STORAGE_TYPE === 'MANUAL') {
        manualUsed += item.USED_PALLETS || 0;
      } else if (isFloorStorage(item)) {
        floorUsed += item.USED_PALLETS || 0;
      } else {
        // BIN storage (has physical rack bins)
        binUsed += item.USED_PALLETS || 0;
      }
    });

    // Virtual used - from virtualWarehouseData filtered by warehouse
    const filteredVirtual = isAll
      ? (virtualWarehouseData || [])
      : (virtualWarehouseData || []).filter((v: any) => v.WAREHOUSE === dashboardWarehouse);
    const virtualUsed = filteredVirtual.reduce((sum: number, item: any) => sum + ((item.TOTAL_SEGMENTS || item.USED_SEGMENTS || 0) - (item.FREE_SEGMENTS || 0)), 0);

    // Capacity from rawWarehouseData (TOTAL_NO_OF_SEGMENTS)
    // Exclude synthetic MANUAL entries (SOURCE='MANUAL') from physical capacity
    const filteredRaw = isAll
      ? rawWarehouseData.filter(item => item.SOURCE !== 'MANUAL')
      : rawWarehouseData.filter(item => item.WAREHOUSE === dashboardWarehouse && item.SOURCE !== 'MANUAL');
    const binCapacity = filteredRaw.reduce((sum, item) => sum + (item.TOTAL_NO_OF_SEGMENTS || 0), 0);

    // Manual capacity: take MAX per warehouse+area, then SUM
    const manualData = data.filter(item => item.STORAGE_TYPE === 'MANUAL');
    const manualCapByArea: Record<string, number> = {};
    manualData.forEach((item) => {
      const key = `${item.WAREHOUSE || ''}__${item.STORAGE_LOCATION || ''}`;
      manualCapByArea[key] = Math.max(manualCapByArea[key] || 0, item.TOTAL_CAPACITY || 0);
    });
    const manualCapacity = Object.values(manualCapByArea).reduce((sum, cap) => sum + cap, 0);
    const totalCapacity = binCapacity + manualCapacity;

    // Temp Blocked bins
    const tempBlockedRaw = isAll
      ? rawWarehouseData.filter(item => (String(item.LPFSTA || '').trim() === '2' || item.OCCUPANCY_STATUS === 'TEMP_BLOCKED'))
      : rawWarehouseData.filter(item => item.WAREHOUSE === dashboardWarehouse && (String(item.LPFSTA || '').trim() === '2' || item.OCCUPANCY_STATUS === 'TEMP_BLOCKED'));
    const tempBlockedBins = tempBlockedRaw.length;
    const tempBlockedPallets = tempBlockedRaw.reduce((sum, item) => sum + (item.TOTAL_NO_OF_SEGMENTS || 0), 0);

    // Area Customer Blocked
    const filteredAreaBlocked = isAll
      ? (areaCustomerBlockedData || [])
      : (areaCustomerBlockedData || []).filter((item: any) => item.WAREHOUSE === dashboardWarehouse);
    const areaBlockedBins = filteredAreaBlocked.length;
    const areaBlockedPallets = filteredAreaBlocked.reduce((sum: number, item: any) => sum + (item.TOTAL_NO_OF_SEGMENTS || 1), 0);

    const totalUsed = binUsed + virtualUsed + floorUsed + manualUsed;

    return {
      totalCapacity,
      totalUsed,
      binUsed,
      binCapacity,
      virtualUsed,
      floorUsed,
      manualUsed,
      manualCapacity,
      uniqueCustomers,
      tempBlockedBins,
      tempBlockedPallets,
      areaBlockedBins,
      areaBlockedPallets,
    };
  }, [dashboardWarehouse, warehouseFilteredCustomerData, rawWarehouseData, virtualWarehouseData, areaCustomerBlockedData, binLocationSet]);

  // KPI values
  const kpis = useMemo(() => {
    let totalCapacity: number;
    let virtualCapacity: number;
    let floorCapacity: number;
    let manualCapacity: number;
    let totalUsed: number;
    let totalCapacityWithManual: number;
    let binCapacity: number;
    let binUsed: number;
    let activeCustomers: number;

    if (computedKpis) {
      totalCapacity = computedKpis.binCapacity;
      binUsed = computedKpis.binUsed;
      binCapacity = computedKpis.binCapacity;
      virtualCapacity = computedKpis.virtualUsed;
      floorCapacity = computedKpis.floorUsed;
      manualCapacity = computedKpis.manualUsed;
      totalUsed = computedKpis.totalUsed;
      totalCapacityWithManual = computedKpis.totalCapacity;
      activeCustomers = computedKpis.uniqueCustomers;
    } else {
      totalCapacity = customerStorageStats?.PHYSICAL_BIN_CAPACITY || 0;
      virtualCapacity = customerStorageStats?.VIRTUAL_USED_PALLETS || 0;
      floorCapacity = customerStorageStats?.FLOOR_USED_PALLETS || 0;
      manualCapacity = customerStorageStats?.MANUAL_PALLETS || 0;
      totalUsed = customerStorageStats?.TOTAL_USED_PALLETS || 0;
      totalCapacityWithManual = customerStorageStats?.TOTAL_CAPACITY_WITH_MANUAL || 0;
      binUsed = customerStorageStats?.BIN_USED_PALLETS || 0;
      binCapacity = customerStorageStats?.PHYSICAL_BIN_CAPACITY || 0;
      activeCustomers = customerStorageStats?.TOTAL_CUSTOMERS || 0;
    }

    const totalAvailable = totalCapacityWithManual - totalUsed;

    return {
      activeCustomers,
      totalCapacity: totalCapacityWithManual,
      utilizedPallets: totalUsed,
      availablePallets: totalAvailable,
      utilizationPct: totalCapacityWithManual > 0 ? Math.round((totalUsed / totalCapacityWithManual) * 100) : 0,
      activeWarehouses: selectedWarehouses.length || stats?.TOTAL_WAREHOUSES || 0,
      binUsed,
      binCapacity,
      virtualUsed: virtualCapacity,
      floorUsed: floorCapacity,
      manualUsed: manualCapacity,
    };
  }, [customerStorageStats, stats, selectedWarehouses, computedKpis]);

  // KPI subtitles with breakdown
  const kpiSubtitles = useMemo(() => {
    const binUsed = computedKpis ? computedKpis.binUsed : (customerStorageStats?.BIN_USED_PALLETS || 0);
    const virtualUsed = computedKpis ? computedKpis.virtualUsed : (customerStorageStats?.VIRTUAL_USED_PALLETS || 0);
    const floorUsed = computedKpis ? computedKpis.floorUsed : (customerStorageStats?.FLOOR_USED_PALLETS || 0);
    const manualUsed = computedKpis ? computedKpis.manualUsed : 0;
    const parts = [`Locations: ${formatNumber(binUsed)}`, `Virtual: ${formatNumber(virtualUsed)}`, `Floor: ${formatNumber(floorUsed)}`];
    if (manualUsed > 0) parts.push(`Manual: ${formatNumber(manualUsed)}`);
    return parts.join(' + ');
  }, [customerStorageStats, computedKpis, formatNumber]);

  // Storage type breakdown
  const storageTypeBreakdown = useMemo(() => {
    let binValue: number;
    let virtualValue: number;
    let floorValue: number;
    let tempBlockedValue: number;
    let areaBlockedValue: number;

    if (computedKpis) {
      binValue = computedKpis.binUsed;
      virtualValue = computedKpis.virtualUsed;
      floorValue = computedKpis.floorUsed;
      tempBlockedValue = computedKpis.tempBlockedPallets;
      areaBlockedValue = computedKpis.areaBlockedPallets;
    } else {
      binValue = customerStorageStats?.BIN_USED_PALLETS || 0;
      virtualValue = customerStorageStats?.VIRTUAL_USED_PALLETS || 0;
      floorValue = customerStorageStats?.FLOOR_USED_PALLETS || 0;
      tempBlockedValue = customerStorageStats?.TEMP_BLOCKED_PALLETS || 0;
      areaBlockedValue = customerStorageStats?.AREA_CUSTOMER_BLOCKED_PALLETS || 0;
    }

    return [
      {
        name: 'Rack BIN',
        value: binValue,
        fill: '#6366f1',
      },
      {
        name: 'Virtual (LKASEG=99)',
        value: virtualValue,
        fill: '#00b4d8',
      },
      {
        name: 'Floor/Bulk',
        value: floorValue,
        fill: '#34c759',
      },
      {
        name: 'Temp Blocked',
        value: tempBlockedValue,
        fill: '#ff9100',
      },
      {
        name: 'Area Blocked',
        value: areaBlockedValue,
        fill: '#9c27b0',
      },
    ].filter((item) => item.value > 0);
  }, [customerStorageStats, computedKpis]);

  // Utilization donut data
  const utilizationData = useMemo(() => {
    return [
      { name: 'Used', value: kpis.utilizedPallets, fill: '#6366f1' },
      { name: 'Free', value: kpis.availablePallets, fill: '#34c759' },
    ];
  }, [kpis]);

  // Storage type pie data
  const storageTypePieData = useMemo(() => {
    return storageTypeBreakdown;
  }, [storageTypeBreakdown]);

  // Warehouse-filtered breakdown stats (for Storage Type Breakdown cards and Grand Total)
  const breakdownStats = useMemo(() => {
    if (computedKpis) {
      return {
        binUsed: computedKpis.binUsed,
        virtualUsed: computedKpis.virtualUsed,
        floorUsed: computedKpis.floorUsed,
        tempBlockedPallets: computedKpis.tempBlockedPallets,
        tempBlockedBins: computedKpis.tempBlockedBins,
        areaBlockedPallets: computedKpis.areaBlockedPallets,
        areaBlockedBins: computedKpis.areaBlockedBins,
        manualPallets: computedKpis.manualUsed,
        manualCapacity: computedKpis.manualCapacity,
        virtualBins: (virtualWarehouseData || []).filter((v: any) => v.WAREHOUSE === dashboardWarehouse).reduce((sum: number, v: any) => sum + (v.TOTAL_BINS || 0), 0),
        virtualSegments: (virtualWarehouseData || []).filter((v: any) => v.WAREHOUSE === dashboardWarehouse).reduce((sum: number, v: any) => sum + (v.TOTAL_SEGMENTS || 0), 0),
        virtualUtilPct: (() => {
          const fv = (virtualWarehouseData || []).filter((v: any) => v.WAREHOUSE === dashboardWarehouse);
          const ts = fv.reduce((s: number, v: any) => s + (v.TOTAL_SEGMENTS || 0), 0);
          const fs = fv.reduce((s: number, v: any) => s + (v.FREE_SEGMENTS || 0), 0);
          return ts > 0 ? Math.round(((ts - fs) / ts) * 100 * 100) / 100 : 0;
        })(),
      };
    }
    return {
      binUsed: customerStorageStats?.BIN_USED_PALLETS || 0,
      virtualUsed: customerStorageStats?.VIRTUAL_USED_PALLETS || 0,
      floorUsed: customerStorageStats?.FLOOR_USED_PALLETS || 0,
      tempBlockedPallets: customerStorageStats?.TEMP_BLOCKED_PALLETS || 0,
      tempBlockedBins: customerStorageStats?.TEMP_BLOCKED_BINS || 0,
      areaBlockedPallets: customerStorageStats?.AREA_CUSTOMER_BLOCKED_PALLETS || 0,
      areaBlockedBins: customerStorageStats?.AREA_CUSTOMER_BLOCKED_BINS || 0,
      manualPallets: customerStorageStats?.MANUAL_PALLETS || 0,
      manualCapacity: customerStorageStats?.MANUAL_CAPACITY || 0,
      virtualBins: virtualStats?.TOTAL_BINS || 0,
      virtualSegments: virtualStats?.TOTAL_SEGMENTS || 0,
      virtualUtilPct: virtualStats?.UTILIZED_PCT || 0,
    };
  }, [computedKpis, customerStorageStats, virtualStats, virtualWarehouseData, dashboardWarehouse]);

  // Capacity: seg 1-2 → as-is, seg 3-9 + level=10 → 2, seg 3-9 + level≠10 → 1, seg ≥10 → as-is.
  // Used: always raw seg - free.
  const THRESHOLD = 10;
  const calcCap = (seg: number, level: number): number => {
    if (seg <= 0) return 0;
    if (seg <= 2) return seg;
    if (seg < THRESHOLD) return level === 10 ? 2 : 1;
    return seg;
  };

  const dynamicCapacityStats = useMemo(() => {
    const isAll = dashboardWarehouse === 'All';
    const whFilter = (wh: string) => isAll || wh === dashboardWarehouse;

    // Per-warehouse accumulators
    type WhRow = {
      warehouse: string;
      rackSmallBins: number; rackLargeBins: number; blockedBins: number; floorBins: number; virtualBins: number;
      rackCap: number; rackUsed: number; blockedCap: number;
      virtualUsed: number; floorPallets: number;
      manualCap: number; manualPallets: number;
      tCap: number; tUsed: number; tFree: number;
      custShortfall: number; // per-warehouse MIN shortfall
    };
    const whMap: Record<string, WhRow> = {};
    const ensureWh = (wh: string) => {
      if (!whMap[wh]) whMap[wh] = {
        warehouse: wh, rackSmallBins: 0, rackLargeBins: 0, blockedBins: 0, floorBins: 0, virtualBins: 0,
        rackCap: 0, rackUsed: 0, blockedCap: 0, virtualUsed: 0, floorPallets: 0,
        manualCap: 0, manualPallets: 0, tCap: 0, tUsed: 0, tFree: 0, custShortfall: 0,
      };
    };

    // 1. Process rawWarehouseData (Rack Small, Rack Large, Blocked, Floor)
    rawWarehouseData.forEach((item: any) => {
      if (item.SOURCE === 'MANUAL') return;
      const wh = (item.WAREHOUSE || '').trim();
      if (!whFilter(wh)) return;
      ensureWh(wh);

      const seg = item.TOTAL_NO_OF_SEGMENTS || 0;
      const free = item.FREE_SEGMENTS || 0;
      const lvl = item.NUMBER_OF_LEVELS || 0;
      const blocked = String(item.LPFSTA || '').trim() === '2' || item.OCCUPANCY_STATUS === 'TEMP_BLOCKED';
      const isFloor = seg === 0;

      if (blocked) {
        whMap[wh].blockedBins += 1;
        whMap[wh].blockedCap += calcCap(seg, lvl);
      } else if (isFloor) {
        whMap[wh].floorBins += 1;
      } else if (seg >= 99) {
        // Virtual from rawWarehouseData — skip, use virtualWarehouseData instead
      } else if (seg > THRESHOLD) {
        whMap[wh].rackLargeBins += 1;
        whMap[wh].rackCap += seg;
        whMap[wh].rackUsed += Math.max(0, seg - free);
      } else {
        whMap[wh].rackSmallBins += 1;
        whMap[wh].rackCap += calcCap(seg, lvl);
        whMap[wh].rackUsed += Math.max(0, seg - free);
      }
    });

    // 2. Virtual used from virtualWarehouseData
    (virtualWarehouseData || []).forEach((item: any) => {
      const wh = (item.WAREHOUSE || '').trim();
      if (!whFilter(wh)) return;
      ensureWh(wh);
      const seg = item.TOTAL_NO_OF_SEGMENTS || item.TOTAL_SEGMENTS || 0;
      const free = item.FREE_SEGMENTS || 0;
      whMap[wh].virtualBins += (item.TOTAL_BINS || 1);
      whMap[wh].virtualUsed += (seg - free);
    });

    // 3. Manual from corrected customer data (MANUAL entries from weekly-entries)
    // Previously used manualEntries prop which could be empty if backend failed
    // Now use MANUAL entries from warehouseFilteredCustomerData (built from weekly-entries latest week)
    const manualCapByWhArea: Record<string, number> = {};
    (warehouseFilteredCustomerData || []).forEach((item: any) => {
      if (item.STORAGE_TYPE !== 'MANUAL') return;
      const wh = (item.WAREHOUSE || '').trim();
      if (!whFilter(wh)) return;
      ensureWh(wh);
      // Capacity: take MAX per warehouse+area (same area has same capacity across clients)
      const areaKey = `${wh}__${item.STORAGE_LOCATION || 'MANUAL'}`;
      manualCapByWhArea[areaKey] = Math.max(manualCapByWhArea[areaKey] || 0, item.TOTAL_CAPACITY || 0);
      // Pallets: SUM across all clients
      whMap[wh].manualPallets += (item.USED_PALLETS || 0);
    });
    // Apply deduplicated capacity
    Object.entries(manualCapByWhArea).forEach(([key, cap]) => {
      const wh = key.split('__')[0];
      if (whMap[wh]) whMap[wh].manualCap += cap;
    });
    // Also include manualEntries prop as fallback (if it has data not in corrected customer data)
    (manualEntries || []).forEach((item: any) => {
      const wh = (item.warehouse || item.WAREHOUSE || '').trim();
      if (!whFilter(wh)) return;
      // Skip if we already have manual data from corrected customer data for this warehouse
      if (whMap[wh] && whMap[wh].manualPallets > 0) return;
      ensureWh(wh);
      whMap[wh].manualCap += (item.capacity || 0);
      whMap[wh].manualPallets += (item.pallets || item.used || 0);
    });

    // 4. Floor pallets from customer data (residual method)
    //    ibmPallets = total non-MANUAL customer USED_PALLETS (physical pallets reported by WMS)
    //    floor = ibmPallets - rackUsed - blockedCap (pallets in warehouse but not in rack/blocked slots)
    const ibmByWh: Record<string, number> = {};
    const clientsByWh: Record<string, Set<string>> = {};
    (warehouseFilteredCustomerData || []).forEach((item: any) => {
      const wh = (item.WAREHOUSE || '').trim();
      if (!whFilter(wh)) return;
      if (!clientsByWh[wh]) clientsByWh[wh] = new Set();
      const cust = (item.CUSTOMER || '').trim();
      if (cust) clientsByWh[wh].add(cust);
      if (item.STORAGE_TYPE === 'MANUAL') return;
      ibmByWh[wh] = (ibmByWh[wh] || 0) + (item.USED_PALLETS || 0);
    });

    // 5. Compute totals per warehouse
    //    Total Used = ibmPallets + virtualUsed + manualPallets (all real pallets)
    //    Sellable = capacity - rackUsed - blocked - manual (empty rack/manual positions for sale)
    //    Note: Total Used CAN exceed Capacity (warehouse with heavy floor storage)
    Object.values(whMap).forEach(row => {
      const ibmPallets = ibmByWh[row.warehouse] || 0;
      row.floorPallets = Math.max(0, ibmPallets - row.rackUsed - row.blockedCap);
      row.tCap = row.rackCap + row.blockedCap + row.manualCap;  // blocked bins are physical capacity
      row.tUsed = row.rackUsed + row.blockedCap + row.virtualUsed + row.manualPallets + row.floorPallets;
      row.tFree = Math.max(0, row.tCap - row.rackUsed - row.blockedCap - row.manualPallets);
    });

    // 6. Customer minimum pallet shortfall — per-warehouse distribution
    // Only include commitments relevant to the current warehouse filter
    let totalCustShortfall = 0;
    const allWhKeys = Object.keys(whMap);
    // Count ALL warehouses from raw data (not just filtered) for correct proration
    const allWarehouseSet = new Set<string>();
    rawWarehouseData.forEach(item => { if (item.WAREHOUSE) allWarehouseSet.add(item.WAREHOUSE); });
    const totalWarehouseCount = Math.max(allWarehouseSet.size, 1);

    (custMinRows || []).forEach(cmr => {
      const customers = cmr.customer.split(',').map(c => c.trim()).filter(Boolean);
      const cmrWarehouses = cmr.warehouse === 'All' ? ['All'] : cmr.warehouse.split(',').map(w => w.trim()).filter(Boolean);

      // Skip commitments that don't apply to the selected warehouse
      if (!isAll && !cmrWarehouses.includes('All') && !cmrWarehouses.includes(dashboardWarehouse)) {
        return; // This commitment is for other warehouses — skip entirely
      }

      let actual = 0;
      customers.forEach(cust => {
        if (cmrWarehouses.includes('All')) {
          if (isAll) {
            actual += (custActualPallets[cust] || 0);
          } else {
            // When filtered to a specific WH, only count that WH's actual for "All" commitments
            actual += (custActualPallets[`${cust}__${dashboardWarehouse}`] || 0);
          }
        } else {
          cmrWarehouses.forEach(wh => {
            if (isAll || wh === dashboardWarehouse) {
              actual += (custActualPallets[`${cust}__${wh}`] || 0);
            }
          });
        }
      });

      // For "All WH" commitments when dashboard is filtered: prorate the minimum
      // Use total warehouse count from ALL raw data, not just filtered whMap
      let effectiveMin = cmr.minPallets;
      if (!isAll && cmrWarehouses.includes('All')) {
        effectiveMin = Math.ceil(cmr.minPallets / totalWarehouseCount);
      }

      const shortfall = Math.max(0, effectiveMin - actual);
      totalCustShortfall += shortfall;
      // Distribute shortfall to warehouse rows
      if (shortfall > 0) {
        if (isAll && cmrWarehouses.includes('All')) {
          // "All" view + "All" commitment: spread evenly
          const perWh = Math.ceil(shortfall / Math.max(allWhKeys.length, 1));
          let remaining = shortfall;
          allWhKeys.forEach(wh => {
            const share = Math.min(perWh, remaining);
            if (whMap[wh]) whMap[wh].custShortfall += share;
            remaining -= share;
          });
        } else if (!isAll) {
          // Filtered view: all shortfall goes to the selected warehouse
          if (whMap[dashboardWarehouse]) whMap[dashboardWarehouse].custShortfall += shortfall;
        } else {
          // "All" view + specific warehouses
          const perWh = Math.ceil(shortfall / cmrWarehouses.length);
          let remaining = shortfall;
          cmrWarehouses.forEach(wh => {
            const share = Math.min(perWh, remaining);
            if (whMap[wh]) whMap[wh].custShortfall += share;
            remaining -= share;
          });
        }
      }
    });

    const warehouseRows = Object.values(whMap).sort((a, b) => a.warehouse.localeCompare(b.warehouse));
    const grandCap = warehouseRows.reduce((s, r) => s + r.tCap, 0);
    // totalUsed = ALL sources (informational display number)
    const grandUsedBase = warehouseRows.reduce((s, r) => s + r.tUsed, 0);
    const grandUsed = grandUsedBase + totalCustShortfall;
    // Sellable = capacity - rack-consuming items - commitment shortfall (committed space isn't sellable)
    const rackOccupied = warehouseRows.reduce((s, r) => s + r.rackUsed + r.blockedCap + r.manualPallets, 0);
    const grandFree = Math.max(0, grandCap - rackOccupied - totalCustShortfall);

    return {
      // Per-type totals for breakdown cards
      rackCap: warehouseRows.reduce((s, r) => s + r.rackCap, 0),
      rackUsed: warehouseRows.reduce((s, r) => s + r.rackUsed, 0),
      blockedCap: warehouseRows.reduce((s, r) => s + r.blockedCap, 0),
      virtualUsed: warehouseRows.reduce((s, r) => s + r.virtualUsed, 0),
      floorPallets: warehouseRows.reduce((s, r) => s + r.floorPallets, 0),
      manualCap: warehouseRows.reduce((s, r) => s + r.manualCap, 0),
      manualPallets: warehouseRows.reduce((s, r) => s + r.manualPallets, 0),
      rackSmallBins: warehouseRows.reduce((s, r) => s + r.rackSmallBins, 0),
      rackLargeBins: warehouseRows.reduce((s, r) => s + r.rackLargeBins, 0),
      blockedBins: warehouseRows.reduce((s, r) => s + r.blockedBins, 0),
      virtualBins: warehouseRows.reduce((s, r) => s + r.virtualBins, 0),
      floorBins: warehouseRows.reduce((s, r) => s + r.floorBins, 0),
      custShortfall: totalCustShortfall,
      // Grand totals
      totalBins: warehouseRows.reduce((s, r) => s + r.rackSmallBins + r.rackLargeBins + r.blockedBins, 0),
      totalCapacity: grandCap,
      totalUsed: grandUsed,
      totalFree: grandFree,
      // Unified location count (rack bins = small + large + blocked) so KPIs don't mix data sources
      totalLocations: warehouseRows.reduce((s, r) => s + r.rackSmallBins + r.rackLargeBins + r.blockedBins, 0),
      // Unified client count across all warehouses
      totalClients: new Set(Object.values(clientsByWh).flatMap(s => Array.from(s))).size,
      warehouseRows,
    };
  }, [rawWarehouseData, virtualWarehouseData, manualEntries, warehouseFilteredCustomerData, dashboardWarehouse, custMinRows, custActualPallets]);

  // Top 10 customers by used pallets
  const topCustomers = useMemo(() => {
    const sorted = [...warehouseFilteredCustomerData].sort((a, b) => (b.USED_PALLETS || 0) - (a.USED_PALLETS || 0));
    return sorted.slice(0, 10).map((item) => ({
      name: item.CUSTOMER,
      used_pallets: item.USED_PALLETS || 0,
    }));
  }, [warehouseFilteredCustomerData]);

  // Segment capacity trend (stacked area chart data)
  const segmentCapacityTrend = useMemo(() => {
    const storageTypeMap: Record<string, number> = {
      Locations: 0,
      Virtual: 0,
      Floor: 0,
      Manual: 0,
    };
    warehouseFilteredCustomerData.forEach((item) => {
      if (item.STORAGE_TYPE === 'MANUAL') {
        storageTypeMap.Manual += item.USED_PALLETS || 0;
      } else if (isFloorStorage(item)) {
        storageTypeMap.Floor += item.USED_PALLETS || 0;
      } else {
        // Locations storage (has physical rack bins)
        storageTypeMap.Locations += item.USED_PALLETS || 0;
      }
    });
    // Add virtual from virtualWarehouseData
    const vData = dashboardWarehouse === 'All' ? (virtualWarehouseData || []) : (virtualWarehouseData || []).filter((v: any) => v.WAREHOUSE === dashboardWarehouse);
    vData.forEach((v: any) => {
      storageTypeMap.Virtual += (v.USED_SEGMENTS || ((v.TOTAL_SEGMENTS || 0) - (v.FREE_SEGMENTS || 0))) || 0;
    });
    return [storageTypeMap];
  }, [warehouseFilteredCustomerData]);

  // Smart insights
  const insights = useMemo(() => {
    const items: {
      icon: string;
      title: string;
      desc: string;
      severity: 'critical' | 'warning' | 'info' | 'success';
    }[] = [];

    if (kpis.utilizationPct > 90) {
      items.push({
        icon: '🚨',
        title: 'Critical Capacity',
        desc: `Overall utilization at ${kpis.utilizationPct}% - immediate action needed`,
        severity: 'critical',
      });
    } else if (kpis.utilizationPct > 80) {
      items.push({
        icon: '⚠️',
        title: 'High Utilization',
        desc: `Overall at ${kpis.utilizationPct}% - plan for expansion`,
        severity: 'warning',
      });
    } else {
      items.push({
        icon: '✅',
        title: 'Healthy Capacity',
        desc: `${formatNumber(kpis.availablePallets)} positions available (${100 - kpis.utilizationPct}%)`,
        severity: 'success',
      });
    }

    if (kpis.virtualUsed > 0) {
      items.push({
        icon: '🔮',
        title: 'Virtual Pallets Need Placement',
        desc: `${formatNumber(kpis.virtualUsed)} pallets in virtual locations need physical bin assignment`,
        severity: 'warning',
      });
    }

    if (breakdownStats.tempBlockedPallets > 0) {
      items.push({
        icon: '🚫',
        title: 'Temp Blocked Bins',
        desc: `${formatNumber(breakdownStats.tempBlockedPallets)} pallets in ${breakdownStats.tempBlockedBins} oversized bins`,
        severity: 'warning',
      });
    }

    if (breakdownStats.areaBlockedPallets > 0) {
      items.push({
        icon: '🔒',
        title: 'Area Customer Blocked',
        desc: `${formatNumber(breakdownStats.areaBlockedPallets)} pallets blocked via LGPGM039`,
        severity: 'info',
      });
    }

    if (breakdownStats.manualPallets > 0) {
      items.push({
        icon: '📝',
        title: 'Manual Entries Active',
        desc: `${formatNumber(breakdownStats.manualPallets)} manual pallets`,
        severity: 'info',
      });
    }

    if (kpis.floorUsed > 0) {
      items.push({
        icon: '🏗️',
        title: 'Floor/Bulk Storage',
        desc: `${formatNumber(kpis.floorUsed)} pallets on floor/bulk - not in rack system`,
        severity: 'info',
      });
    }

    warehouseCards.forEach((wh) => {
      if (wh.util_percent > 90) {
        items.push({
          icon: '🏭',
          title: `${getWarehouseDisplayName(wh.warehouse)} Critical`,
          desc: `Utilization at ${wh.util_percent}% - ${formatNumber(wh.free_pallets)} positions left`,
          severity: 'critical',
        });
      }
    });

    if (stats && stats.AVG_LEVELS > 0) {
      items.push({
        icon: '📊',
        title: 'Rack Level Summary',
        desc: `Avg ${stats.AVG_LEVELS.toFixed(1)} levels (range: ${stats.MIN_LEVELS}-${stats.MAX_LEVELS})`,
        severity: 'info',
      });
    }

    return items;
  }, [kpis, breakdownStats, warehouseCards, stats, formatNumber, getWarehouseDisplayName]);

  // ═══════════════════════════════════════════════════════════
  // OPERATIONS CONSOLE: computed data for the Overview tab
  // ═══════════════════════════════════════════════════════════
  const ocData = useMemo(() => {
    // 1. Filter rawWarehouseData based on OC filters (exclude virtual seg>=99, floor seg===0)
    let bins = (rawWarehouseData || []).filter((item: any) => {
      if (item.SOURCE === 'MANUAL') return false;
      const seg = item.TOTAL_NO_OF_SEGMENTS || 0;
      if (seg >= 99) return false; // exclude virtual
      // seg===0 is floor — exclude per user request
      if (seg === 0) return false;
      return true;
    });

    // Apply warehouse filter
    if (dashboardWarehouse !== 'All') {
      bins = bins.filter((b: any) => (b.WAREHOUSE || '').trim() === dashboardWarehouse);
    }
    // Apply chamber filter
    if (ocChamber !== 'All') {
      bins = bins.filter((b: any) => (b.STORAGE_AREA || '').trim() === ocChamber);
    }
    // Apply pallet/shelf type filter
    if (ocPalletType !== 'All') {
      bins = bins.filter((b: any) => String(b.SHELF_SIZE || '').trim() === ocPalletType);
    }
    // Apply blocked filter
    if (ocExcludeBlocked) {
      bins = bins.filter((b: any) => {
        const blocked = String(b.LPFSTA || '').trim() === '2' || b.OCCUPANCY_STATUS === 'TEMP_BLOCKED';
        return !blocked;
      });
    }

    // 2. Unique lists for filter dropdowns
    const allChambers = Array.from(new Set((rawWarehouseData || [])
      .filter((b: any) => b.SOURCE !== 'MANUAL' && (b.TOTAL_NO_OF_SEGMENTS || 0) < 99 && (b.TOTAL_NO_OF_SEGMENTS || 0) > 0)
      .map((b: any) => (b.STORAGE_AREA || '').trim()))).filter(Boolean).sort() as string[];
    const allPalletTypes = Array.from(new Set((rawWarehouseData || [])
      .filter((b: any) => b.SOURCE !== 'MANUAL' && (b.TOTAL_NO_OF_SEGMENTS || 0) < 99 && (b.TOTAL_NO_OF_SEGMENTS || 0) > 0)
      .map((b: any) => String(b.SHELF_SIZE || '').trim()))).filter(Boolean).sort() as string[];
    const allWarehouses = Array.from(new Set((rawWarehouseData || [])
      .filter((b: any) => b.SOURCE !== 'MANUAL')
      .map((b: any) => (b.WAREHOUSE || '').trim()))).filter(Boolean).sort() as string[];

    // 3. Cap = raw seg, Used = raw seg - free (matches WMS).

    // 4. Per-bin enrichment
    const enriched = bins.map((b: any) => {
      const seg = b.TOTAL_NO_OF_SEGMENTS || 0;
      const free = b.FREE_SEGMENTS || 0;
      const lvl = b.NUMBER_OF_LEVELS || 0;
      const blocked = String(b.LPFSTA || '').trim() === '2' || b.OCCUPANCY_STATUS === 'TEMP_BLOCKED';
      const cap = calcCap(seg, lvl);
      const used = blocked ? cap : Math.max(0, seg - free);
      return {
        ...b,
        wh: (b.WAREHOUSE || '').trim(),
        chamber: (b.STORAGE_AREA || '').trim(),
        shelfSize: String(b.SHELF_SIZE || '').trim(),
        seg, free, lvl, cap, used,
        freePal: Math.max(0, cap - used),
        blocked,
      };
    });

    // 5. Totals
    const totalLocationsUnique = enriched.length; // each bin = 1 unique location
    const totalLocationsIncSeg = enriched.reduce((s: number, b: any) => s + b.seg, 0);
    const totalCapacity = enriched.reduce((s: number, b: any) => s + b.cap, 0);
    const totalUsed = enriched.reduce((s: number, b: any) => s + b.used, 0);
    const totalFree = enriched.reduce((s: number, b: any) => s + b.freePal, 0);
    const blockedCount = enriched.filter((b: any) => b.blocked).length;
    const blockedCap = enriched.filter((b: any) => b.blocked).reduce((s: number, b: any) => s + b.cap, 0);
    const sellableLocations = enriched.filter((b: any) => !b.blocked).length;
    const sellableCap = totalCapacity - blockedCap;
    const utilPct = totalCapacity > 0 ? Math.round((totalUsed / totalCapacity) * 100 * 10) / 10 : 0;

    // 6. Per-warehouse breakdown
    const whMap: Record<string, any> = {};
    enriched.forEach((b: any) => {
      if (!whMap[b.wh]) whMap[b.wh] = { warehouse: b.wh, locations: 0, segments: 0, cap: 0, used: 0, free: 0, blocked: 0, blockedCap: 0, chambers: new Set() };
      const w = whMap[b.wh];
      w.locations++; w.segments += b.seg; w.cap += b.cap; w.used += b.used; w.free += b.freePal;
      if (b.blocked) { w.blocked++; w.blockedCap += b.cap; }
      w.chambers.add(b.chamber);
    });
    const warehouseBreakdown = Object.values(whMap).map((w: any) => ({
      ...w, chambers: w.chambers.size,
      utilPct: w.cap > 0 ? Math.round((w.used / w.cap) * 100 * 10) / 10 : 0,
      sellable: w.cap - w.blockedCap,
    })).sort((a: any, b: any) => b.cap - a.cap);

    // 7. Per-chamber breakdown
    const chmMap: Record<string, any> = {};
    enriched.forEach((b: any) => {
      if (!chmMap[b.chamber]) chmMap[b.chamber] = { chamber: b.chamber, wh: b.wh, locations: 0, segments: 0, cap: 0, used: 0, free: 0, blocked: 0, blockedCap: 0 };
      const c = chmMap[b.chamber];
      c.locations++; c.segments += b.seg; c.cap += b.cap; c.used += b.used; c.free += b.freePal;
      if (b.blocked) { c.blocked++; c.blockedCap += b.cap; }
    });
    const chamberBreakdown = Object.values(chmMap).map((c: any) => ({
      ...c, utilPct: c.cap > 0 ? Math.round((c.used / c.cap) * 100 * 10) / 10 : 0,
      sellable: c.cap - c.blockedCap,
    })).sort((a: any, b: any) => b.cap - a.cap);

    // 8. Per-client breakdown (from customerStorageData, filtered by warehouse)
    let custData = (correctedCustomerStorageData || []).filter((c: any) => {
      if (c.STORAGE_TYPE === 'VIRTUAL' || c.STORAGE_TYPE === 'FLOOR' || c.STORAGE_TYPE === 'BULK') return false;
      return true;
    });
    if (dashboardWarehouse !== 'All') {
      custData = custData.filter((c: any) => c.WAREHOUSE === dashboardWarehouse);
    }
    if (ocChamber !== 'All') {
      custData = custData.filter((c: any) => (c.STORAGE_LOCATION || '') === ocChamber);
    }
    // Fixed-space clients from commitments
    const fixedSpaceClients = new Set(custMinRows.map((r: CustMinRow) => r.customer.split(',').map(s => s.trim())).flat());
    if (ocExcludeFixedSpace) {
      custData = custData.filter((c: any) => !fixedSpaceClients.has(c.CUSTOMER));
    }

    const clientMap: Record<string, any> = {};
    custData.forEach((c: any) => {
      const key = c.CUSTOMER || 'Unknown';
      if (!clientMap[key]) clientMap[key] = { client: key, warehouses: new Set(), used: 0, cap: 0, areas: new Set(), isFixed: fixedSpaceClients.has(key) };
      const cl = clientMap[key];
      cl.used += (c.USED_PALLETS || 0);
      cl.cap += (c.TOTAL_CAPACITY || 0);
      cl.warehouses.add(c.WAREHOUSE || '');
      cl.areas.add(c.STORAGE_LOCATION || '');
    });
    const clientBreakdown = Object.values(clientMap).map((cl: any) => ({
      ...cl, warehouses: [...cl.warehouses].filter(Boolean), areas: cl.areas.size,
      free: Math.max(0, cl.cap - cl.used),
      utilPct: cl.cap > 0 ? Math.round((cl.used / cl.cap) * 100 * 10) / 10 : 0,
    })).sort((a: any, b: any) => b.used - a.used);

    // 9. All unique client names for filter dropdown
    const allClients = Array.from(new Set((correctedCustomerStorageData || [])
      .filter((c: any) => c.STORAGE_TYPE !== 'VIRTUAL' && c.STORAGE_TYPE !== 'FLOOR')
      .map((c: any) => (c.CUSTOMER || '').trim()))).filter(Boolean).sort() as string[];

    // 10. Pallet type distribution (with blocked + sellable)
    const palletTypeMap: Record<string, { type: string; locations: number; cap: number; used: number; blocked: number; blockedCap: number }> = {};
    enriched.forEach((b: any) => {
      const pt = b.shelfSize || 'Unknown';
      if (!palletTypeMap[pt]) palletTypeMap[pt] = { type: pt, locations: 0, cap: 0, used: 0, blocked: 0, blockedCap: 0 };
      palletTypeMap[pt].locations++; palletTypeMap[pt].cap += b.cap; palletTypeMap[pt].used += b.used;
      if (b.blocked) { palletTypeMap[pt].blocked++; palletTypeMap[pt].blockedCap += b.cap; }
    });
    const palletTypeBreakdown = Object.values(palletTypeMap).map(p => ({
      ...p,
      sellable: Math.max(0, p.cap - p.blockedCap - p.used),
    })).sort((a, b) => b.cap - a.cap);

    return {
      enriched, totalLocationsUnique, totalLocationsIncSeg, totalCapacity, totalUsed, totalFree,
      blockedCount, blockedCap, sellableLocations, sellableCap, utilPct,
      warehouseBreakdown, chamberBreakdown, clientBreakdown, palletTypeBreakdown,
      allChambers, allPalletTypes, allWarehouses, allClients, fixedSpaceClients,
    };
  }, [rawWarehouseData, correctedCustomerStorageData, custMinRows, dashboardWarehouse, ocChamber, ocPalletType, ocExcludeBlocked, ocExcludeFixedSpace]);

  const handleSort = (field: string) => {
    setCustomerSort((prev) => ({
      field,
      asc: prev.field === field ? !prev.asc : true,
    }));
  };

  const exportToExcel = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `executive-dashboard-${timestamp}.csv`;
    const csvRows: string[] = [];

    csvRows.push('EXECUTIVE DASHBOARD EXPORT');
    csvRows.push(`Generated: ${new Date().toLocaleString()}`);
    csvRows.push('');

    csvRows.push('KPI SUMMARY');
    csvRows.push(`Active Customers,${kpis.activeCustomers}`);
    csvRows.push(`Total Capacity,${kpis.totalCapacity}`);
    csvRows.push(`Utilized Pallets,${kpis.utilizedPallets}`);
    csvRows.push(`Available Pallets,${kpis.availablePallets}`);
    csvRows.push(`Utilization Rate,${kpis.utilizationPct}%`);
    csvRows.push(`BIN Used / Capacity,${kpis.binUsed} / ${kpis.binCapacity}`);
    csvRows.push(`Virtual Used,${kpis.virtualUsed}`);
    csvRows.push(`Floor Used,${kpis.floorUsed}`);
    csvRows.push(`Manual Used,${kpis.manualUsed}`);
    csvRows.push('');

    csvRows.push('CUSTOMER DATA');
    csvRows.push('Customer,Warehouse,Storage Location,Storage Type,Used Pallets,Total Capacity,Available Capacity');
    paginatedCustomerData.forEach((row) => {
      csvRows.push(
        `${row.CUSTOMER},${row.WAREHOUSE},${row.STORAGE_LOCATION},${row.STORAGE_TYPE},${row.USED_PALLETS},${row.TOTAL_CAPACITY},${row.AVAILABLE_CAPACITY}`
      );
    });
    csvRows.push('');

    csvRows.push('WAREHOUSE ANALYSIS');
    csvRows.push('Warehouse,Capacity,Used,Free,Area Count,Utilization %');
    warehouseCards.forEach((wh) => {
      csvRows.push(
        `${getWarehouseDisplayName(wh.warehouse)},${wh.total_capacity},${wh.used_pallets},${wh.free_pallets},${wh.area_count},${wh.util_percent}`
      );
    });
    csvRows.push('');

    csvRows.push('AREA ANALYSIS');
    csvRows.push('Area,Bins,Total Segments,Used Segments,Free Segments,Avg Levels,Utilization %');
    areaAnalysis.forEach((area) => {
      csvRows.push(
        `${area.area},${area.bins},${area.total_segments},${area.used_segments},${area.total_segments - area.used_segments},${area.avg_levels},${area.util_percent}`
      );
    });

    const csv = csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  if (loading || !stats || !customerStorageStats) {
    return (
      <div className="ed-loading-container">
        <div className="ed-spinner"></div>
        <p className="ed-loading-text">Loading Executive Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="ed-container">
      <div className="ed-top-bar">
        <div className="ed-title-section">
          <h1 className="ed-title">Executive Dashboard</h1>
          <p className="ed-subtitle">Last updated: {lastRefreshTime}</p>
        </div>
        <div className="ed-action-buttons">
          <button className="ed-btn ed-btn-primary" onClick={onRefresh} title="Refresh data">
            <span className="ed-btn-icon">🔄</span> Refresh
          </button>
          <button className="ed-btn ed-btn-secondary" onClick={onResetFilters} title="Reset all filters">
            <span className="ed-btn-icon">↻</span> Reset
          </button>
          <button className="ed-btn ed-btn-accent" onClick={onOpenAI} title="AI Analysis">
            <span className="ed-btn-icon">⚡</span> AI Analysis
          </button>
          <button className="ed-btn ed-btn-secondary" onClick={() => exportToExcel()} title="Export to Excel">
            <span className="ed-btn-icon">📊</span> Export
          </button>
          <button className="ed-btn ed-btn-secondary" onClick={() => window.print()} title="Print dashboard">
            <span className="ed-btn-icon">🖨️</span> Print
          </button>
        </div>
      </div>

      <div className="ed-tabs">
        <button className={`ed-tab ${activeTab === 'overview' ? 'ed-tab-active' : ''}`} onClick={() => setActiveTab('overview')}>
          Overview
        </button>
        <button className={`ed-tab ${activeTab === 'customers' ? 'ed-tab-active' : ''}`} onClick={() => setActiveTab('customers')}>
          Customers
        </button>
        <button className={`ed-tab ${activeTab === 'warehouses' ? 'ed-tab-active' : ''}`} onClick={() => setActiveTab('warehouses')}>
          Warehouses
        </button>
        <button className={`ed-tab ${activeTab === 'analytics' ? 'ed-tab-active' : ''}`} onClick={() => setActiveTab('analytics')}>
          Analytics
        </button>
        <button className={`ed-tab ${activeTab === 'dynamic' ? 'ed-tab-active' : ''}`} onClick={() => setActiveTab('dynamic')} style={activeTab === 'dynamic' ? { borderBottomColor: '#0ea5e9', color: '#0ea5e9' } : {}}>
          Dynamic Capacity
        </button>
      </div>

      <div className="ed-warehouse-filter-bar">
        <span className="ed-filter-label">Filter by Warehouse:{!isAdminProp && userWarehouse ? ` (${getWarehouseDisplayName(userWarehouse)})` : ''}</span>
        <div className="ed-warehouse-filter-chips">
          {(!isAdminProp && userWarehouse)
            ? (
              <button className="ed-filter-chip ed-filter-chip-active" style={{ cursor: 'default' }}>
                {getWarehouseDisplayName(userWarehouse)}
              </button>
            )
            : ['All', ...warehouseData.map((wh: any) => wh.WAREHOUSE)].map((warehouse) => (
              <button
                key={warehouse}
                className={`ed-filter-chip ${dashboardWarehouse === warehouse ? 'ed-filter-chip-active' : ''}`}
                onClick={() => setDashboardWarehouse(warehouse)}
              >
                {warehouse === 'All' ? 'All Warehouses' : getWarehouseDisplayName(warehouse)}
              </button>
            ))
          }
        </div>
      </div>

      {activeTab === 'overview' && (() => {
        // DATA — dynamicCapacityStats for grand totals (all sources), ocData for chamber/pallet/client detail
        const dc = dynamicCapacityStats;
        const oc = ocData;
        // Grand totals from dynamicCapacityStats
        const grandCap = dc.totalCapacity;    // = rackCap + manualCap
        const grandUsed = dc.totalUsed;       // = ALL sources (rack+blocked+virtual+floor+manual+commitment) — informational
        const grandFree = dc.totalFree;       // = max(0, cap - rackOccupied) — ONLY rack-consuming items deducted
        // Sellable = rack+manual positions that are empty & unblocked (floor/virtual don't consume rack space)
        const sellableSpace = grandFree;
        // Utilization % = rack-based occupancy / capacity (not including floor/virtual which don't use rack positions)
        const rackOccupied = dc.rackUsed + dc.blockedCap + dc.manualPallets;
        const utilPct = grandCap > 0 ? Math.min(100, Math.round((rackOccupied / grandCap) * 1000) / 10) : 0;
        // Gauge needle math (semicircle)
        const gCx = 100, gCy = 90, gR = 65;
        const needleA = Math.PI - (Math.min(utilPct, 100) / 100) * Math.PI;
        const nX = gCx + gR * 0.85 * Math.cos(needleA);
        const nY = gCy - gR * 0.85 * Math.sin(needleA);
        // Donut — Occupied (rack+floor+manual) / Sellable (free) / Blocked / Virtual + Commitment
        // These should add up: Occupied + Sellable + Blocked + Virtual + Commitment ≈ grandCap + virtualUsed + custShortfall
        const occupiedPallets = dc.rackUsed + dc.floorPallets + dc.manualPallets;
        const donutData = [
          { name: 'Occupied', value: occupiedPallets, fill: '#e67e22' },
          { name: 'Sellable', value: sellableSpace, fill: '#2ecc71' },
          { name: 'Blocked', value: dc.blockedCap, fill: '#e74c3c' },
          { name: 'Virtual', value: dc.virtualUsed, fill: '#3498db' },
          { name: 'Commitment', value: dc.custShortfall, fill: '#9b59b6' },
        ].filter(d => d.value > 0);
        // Warehouse utilization bars (like reference "by Department")
        const whBars = dc.warehouseRows.slice(0, 8).map((w: any) => {
          const rackOcc = w.rackUsed + w.blockedCap + w.manualPallets;
          return {
            name: getWarehouseDisplayName(w.warehouse).substring(0, 14),
            pct: w.tCap > 0 ? Math.min(100, Math.round((rackOcc / w.tCap) * 1000) / 10) : 0,
          };
        });
        // Client summary
        const clientRows = (ocClient === 'All' ? oc.clientBreakdown : oc.clientBreakdown.filter((c: any) => c.client === ocClient)).slice(0, 25);
        // Chamber/pallet
        const chambers = oc.chamberBreakdown;
        const palletTypes = oc.palletTypeBreakdown;
        // Select style for filter panel (reference right side)
        const selS: React.CSSProperties = { width: '100%', padding: '5px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '10px', fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer' };
        void kpis; void breakdownStats; void warehousePerformance;
        // Data verification log
        console.log('📊 SALES DASHBOARD DATA CHECK:', {
          grandCap, grandUsed, grandFree, sellableSpace, utilPct,
          rackUsed: dc.rackUsed, blockedCap: dc.blockedCap,
          virtualUsed: dc.virtualUsed, floorPallets: dc.floorPallets,
          manualPallets: dc.manualPallets, custShortfall: dc.custShortfall,
          totalLocations: dc.totalLocations, totalClients: dc.totalClients,
          checkSum: `rack(${dc.rackUsed}) + blocked(${dc.blockedCap}) + virtual(${dc.virtualUsed}) + floor(${dc.floorPallets}) + manual(${dc.manualPallets}) + commit(${dc.custShortfall}) = ${dc.rackUsed + dc.blockedCap + dc.virtualUsed + dc.floorPallets + dc.manualPallets + dc.custShortfall} vs grandUsed(${grandUsed})`,
          sellableCheck: `cap(${grandCap}) - used(${grandUsed}) = ${grandCap - grandUsed} vs sellable(${sellableSpace})`,
        });

        return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '85vh', background: '#f5f5f0', margin: '-20px', fontFamily: 'Segoe UI, Arial, sans-serif' }}>

          {/* ═══ TOP HEADER (dark bar with orange accent — exactly like reference) ═══ */}
          <div style={{ background: '#2c2c2c', padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '30px', height: '30px', borderRadius: '6px', background: 'linear-gradient(135deg, #e67e22, #f39c12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#fff', fontWeight: 800, fontSize: '13px' }}>H</span>
              </div>
              <span style={{ fontSize: '14px', fontWeight: 700, color: '#e67e22', letterSpacing: '0.5px' }}>Hala Warehousing — Operations Console</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '10px', color: '#999' }}>Last updated: {lastRefreshTime}</span>
              <button onClick={onRefresh} style={{ padding: '4px 14px', borderRadius: '4px', background: '#e67e22', color: '#fff', border: 'none', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>Refresh</button>
            </div>
          </div>

          {/* ═══ THREE-COLUMN LAYOUT: Sidebar + Content + Filters (exactly like reference) ═══ */}
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

            {/* ═══ LEFT SIDEBAR (dark nav — reference: Overview, Space Usage, Departmental, Location...) ═══ */}
            <div style={{ width: '155px', background: '#1e1e1e', padding: '14px 0', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <div style={{ padding: '6px 12px', marginBottom: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #e67e22, #d35400)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 4px' }}>
                  <span style={{ color: '#fff', fontWeight: 800, fontSize: '14px' }}>H</span>
                </div>
              </div>
              {[
                { label: 'Overview', active: true },
                { label: 'Warehouse', active: false },
                { label: 'Chamber', active: false },
                { label: 'Clients', active: false },
                { label: 'Pallet Type', active: false },
              ].map((nav, i) => (
                <div key={i} style={{
                  padding: '9px 14px', cursor: 'default',
                  background: nav.active ? 'rgba(230,126,34,0.15)' : 'transparent',
                  borderLeft: nav.active ? '3px solid #e67e22' : '3px solid transparent',
                }}>
                  <span style={{ fontSize: '11px', fontWeight: nav.active ? 700 : 400, color: nav.active ? '#e67e22' : '#888' }}>{nav.label}</span>
                </div>
              ))}
            </div>

            {/* ═══ CENTER CONTENT ═══ */}
            <div style={{ flex: 1, padding: '18px 20px', overflow: 'auto', background: '#f5f5f0' }}>

              {/* ── 5 KPI STRIP (reference: 118 | 4.2 | 30 | 21 | 29) ── */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '18px' }}>
                {[
                  { val: formatNumber(grandCap), label: 'Total Capacity', sub: 'rack + manual' },
                  { val: formatNumber(grandUsed), label: 'Total Used', sub: 'all sources' },
                  { val: formatNumber(sellableSpace), label: 'Sellable Space', sub: 'available for sale' },
                  { val: formatNumber(dc.totalLocations), label: 'Locations', sub: 'rack bins' },
                  { val: String(dc.totalClients), label: 'Active Clients', sub: 'customers' },
                ].map((k, i) => (
                  <div key={i} style={{ background: '#fff', borderRadius: '8px', padding: '14px 10px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                    <div style={{ fontSize: '26px', fontWeight: 800, color: '#2c2c2c', lineHeight: 1 }}>{k.val}</div>
                    <div style={{ fontSize: '9px', fontWeight: 600, color: '#888', marginTop: '5px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{k.label}</div>
                    <div style={{ fontSize: '8px', color: '#bbb', marginTop: '2px' }}>{k.sub}</div>
                  </div>
                ))}
              </div>

              {/* ── ROW 2: Gauge | Donut | Warehouse Bars (exactly like reference middle row) ── */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr 1fr', gap: '14px', marginBottom: '18px' }}>

                {/* SPEEDOMETER GAUGE — Utilization Rate % */}
                <div style={{ background: '#fff', borderRadius: '8px', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#555', marginBottom: '8px', textAlign: 'center' }}>Utilization Rate (%)</div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <svg width="200" height="115" viewBox="0 0 200 115">
                      <defs>
                        <linearGradient id="ocGaugeG" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#2ecc71" />
                          <stop offset="50%" stopColor="#f1c40f" />
                          <stop offset="100%" stopColor="#e74c3c" />
                        </linearGradient>
                      </defs>
                      <path d="M 20 95 A 80 80 0 0 1 180 95" fill="none" stroke="#e8e8e8" strokeWidth="14" strokeLinecap="round" />
                      <path d="M 20 95 A 80 80 0 0 1 180 95" fill="none" stroke="url(#ocGaugeG)" strokeWidth="14" strokeLinecap="round" pathLength="100" strokeDasharray={`${utilPct} 100`} />
                      <line x1={gCx} y1={gCy} x2={nX} y2={nY} stroke="#2c2c2c" strokeWidth="2" strokeLinecap="round" />
                      <circle cx={gCx} cy={gCy} r="4" fill="#2c2c2c" />
                      <text x={gCx} y={gCy + 18} textAnchor="middle" fontSize="20" fontWeight="800" fill="#2c2c2c">{utilPct}%</text>
                    </svg>
                  </div>
                </div>

                {/* DONUT — Usage Status Distribution (reference: 3D-style donut) */}
                <div style={{ background: '#fff', borderRadius: '8px', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#555', marginBottom: '4px', textAlign: 'center' }}>Usage Status Distribution</div>
                  <div style={{ display: 'flex', alignItems: 'center', height: '130px' }}>
                    <div style={{ flex: 1 }}>
                      <ResponsiveContainer width="100%" height={130}>
                        <PieChart>
                          <Pie data={donutData} cx="50%" cy="50%" innerRadius={32} outerRadius={55} paddingAngle={2} dataKey="value" isAnimationActive={false}>
                            {donutData.map((d, idx) => <Cell key={idx} fill={d.fill} />)}
                          </Pie>
                          <Tooltip formatter={(v: any) => formatNumber(Number(v))} contentStyle={{ fontSize: '10px', borderRadius: '4px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '105px' }}>
                      {donutData.map((d, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: d.fill, flexShrink: 0 }} />
                          <span style={{ color: '#777' }}>{d.name}: <b style={{ color: '#333' }}>{formatNumber(d.value)}</b></span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* HORIZONTAL BARS — Utilization by Warehouse (reference: "by Department") */}
                <div style={{ background: '#fff', borderRadius: '8px', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#555', marginBottom: '8px' }}>Utilization (%) by Warehouse</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {whBars.map((w: any, i: number) => (
                      <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#777', marginBottom: '2px' }}>
                          <span>{w.name}</span>
                          <span style={{ fontWeight: 700, color: w.pct > 85 ? '#e74c3c' : w.pct > 70 ? '#f39c12' : '#2ecc71' }}>{w.pct}%</span>
                        </div>
                        <div style={{ height: '6px', background: '#eee', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min(100, w.pct)}%`, background: w.pct > 85 ? '#e74c3c' : w.pct > 70 ? '#f39c12' : '#2ecc71', borderRadius: '3px' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── ROW 3: Source Breakdown + Chamber + Pallet Type ── */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 0.8fr', gap: '14px', marginBottom: '18px' }}>

                {/* SOURCE BREAKDOWN — shows where used pallets come from */}
                <div style={{ background: '#fff', borderRadius: '8px', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#555', marginBottom: '8px' }}>Used Pallets by Source</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {[
                      { label: 'Rack', value: dc.rackUsed, color: '#6366f1' },
                      { label: 'Blocked', value: dc.blockedCap, color: '#f59e0b' },
                      { label: 'Virtual', value: dc.virtualUsed, color: '#0ea5e9' },
                      { label: 'Floor', value: dc.floorPallets, color: '#10b981' },
                      { label: 'Manual', value: dc.manualPallets, color: '#a855f7' },
                      { label: 'Commit Gap', value: dc.custShortfall, color: '#ec4899' },
                    ].filter(s => s.value > 0).map((s, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: s.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '10px', color: '#777', flex: 1 }}>{s.label}</span>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: '#333' }}>{formatNumber(s.value)}</span>
                        <div style={{ width: '50px', height: '4px', background: '#eee', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${grandUsed > 0 ? Math.round((s.value / grandUsed) * 100) : 0}%`, background: s.color, borderRadius: '2px' }} />
                        </div>
                      </div>
                    ))}
                    <div style={{ borderTop: '1px solid #eee', paddingTop: '4px', marginTop: '2px', display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
                      <span style={{ fontWeight: 700, color: '#555' }}>Grand Total</span>
                      <span style={{ fontWeight: 800, color: '#e67e22' }}>{formatNumber(grandUsed)}</span>
                    </div>
                  </div>
                </div>

                {/* CHAMBER TABLE */}
                <div style={{ background: '#fff', borderRadius: '8px', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#555', marginBottom: '8px' }}>Chamber / Area Breakdown</div>
                  <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #eee' }}>
                          {['Chamber', 'Site', 'Used', 'Sellable', '%'].map((h, hi) => (
                            <th key={hi} style={{ padding: '4px 6px', textAlign: hi > 1 ? 'right' : 'left', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {chambers.slice(0, 15).map((c: any, ci: number) => (
                          <tr key={ci} style={{ background: ci % 2 === 0 ? '#fff' : '#fafaf8', borderBottom: '1px solid #f5f5f5' }}>
                            <td style={{ padding: '4px 6px', fontWeight: 600, color: '#2c2c2c' }}>{c.chamber}</td>
                            <td style={{ padding: '4px 6px', color: '#888', fontSize: '8px' }}>{getWarehouseDisplayName(c.wh)}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', color: '#e67e22', fontWeight: 600 }}>{formatNumber(c.used)}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', color: '#2ecc71', fontWeight: 600 }}>{formatNumber(Math.max(0, (c.sellable || c.cap) - c.used))}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                              <span style={{ fontWeight: 700, color: c.utilPct > 85 ? '#e74c3c' : c.utilPct > 70 ? '#f39c12' : '#2ecc71' }}>{c.utilPct}%</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* PALLET TYPE — with sellable per type */}
                <div style={{ background: '#fff', borderRadius: '8px', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#555', marginBottom: '8px' }}>Sellable by Pallet Type</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {palletTypes.map((p: any, pi: number) => {
                      const pSellable = p.sellable || Math.max(0, p.cap - (p.blockedCap || 0) - p.used);
                      const pPct = p.cap > 0 ? Math.round((p.used / p.cap) * 100) : 0;
                      const sellPct = p.cap > 0 ? Math.round((pSellable / p.cap) * 100) : 0;
                      return (
                        <div key={pi} style={{ borderBottom: pi < palletTypes.length - 1 ? '1px solid #f5f5f5' : 'none', paddingBottom: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#2c2c2c' }}>{p.type}</span>
                            <span style={{ fontSize: '9px', color: '#999' }}>{formatNumber(p.locations)} loc</span>
                          </div>
                          {/* Stacked bar: Used (orange) + Sellable (green) + Blocked (red) = Cap */}
                          <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden', marginBottom: '4px', background: '#eee' }}>
                            <div style={{ height: '100%', width: `${pPct}%`, background: '#e67e22' }} title={`Used: ${formatNumber(p.used)}`} />
                            <div style={{ height: '100%', width: `${sellPct}%`, background: '#2ecc71' }} title={`Sellable: ${formatNumber(pSellable)}`} />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px' }}>
                            <span style={{ color: '#e67e22', fontWeight: 600 }}>Used: {formatNumber(p.used)}</span>
                            <span style={{ color: '#2ecc71', fontWeight: 700 }}>Sellable: {formatNumber(pSellable)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '8px', color: '#bbb', marginTop: '1px' }}>
                            <span>Cap: {formatNumber(p.cap)}</span>
                            {(p.blockedCap || 0) > 0 && <span style={{ color: '#e74c3c' }}>Blocked: {formatNumber(p.blockedCap)}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* TOTAL SELLABLE BY PALLET TYPE */}
                  <div style={{ borderTop: '2px solid #eee', marginTop: '6px', paddingTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#555' }}>Total Sellable</span>
                    <span style={{ fontSize: '14px', fontWeight: 800, color: '#2ecc71' }}>{formatNumber(palletTypes.reduce((s: number, p: any) => s + (p.sellable || Math.max(0, p.cap - (p.blockedCap || 0) - p.used)), 0))}</span>
                  </div>
                </div>
              </div>

              {/* ── ROW 4: Client Summary Table ── */}
              <div style={{ background: '#fff', borderRadius: '8px', padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#555' }}>Client Occupancy Summary</div>
                  <span style={{ fontSize: '9px', background: '#e67e22', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontWeight: 700 }}>{clientRows.length} clients</span>
                </div>
                <div style={{ overflowX: 'auto', maxHeight: '250px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #eee' }}>
                        {['Client', 'Warehouse', 'Used Pallets', 'Sellable', 'Util %'].map((h, hi) => (
                          <th key={hi} style={{ padding: '5px 8px', textAlign: hi > 1 ? 'right' : 'left', fontWeight: 700, color: '#888', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {clientRows.map((cl: any, ci: number) => (
                        <tr key={ci} style={{ background: ci % 2 === 0 ? '#fff' : '#fafaf8', borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '6px 8px', fontWeight: 600, color: '#2c2c2c' }}>
                            {cl.client}
                            {cl.isFixed && <span style={{ marginLeft: '5px', fontSize: '8px', background: '#fff3e0', color: '#e67e22', padding: '1px 4px', borderRadius: '3px', fontWeight: 700 }}>Fixed</span>}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#666', fontSize: '9px' }}>{cl.warehouses.map((w: string) => getWarehouseDisplayName(w)).join(', ')}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#e67e22' }}>{formatNumber(cl.used)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#2ecc71' }}>{formatNumber(cl.free)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'flex-end' }}>
                              <div style={{ width: '36px', height: '5px', background: '#eee', borderRadius: '2px', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${Math.min(100, cl.utilPct)}%`, background: cl.utilPct > 85 ? '#e74c3c' : cl.utilPct > 70 ? '#f39c12' : '#2ecc71', borderRadius: '2px' }} />
                              </div>
                              <span style={{ fontWeight: 700, color: cl.utilPct > 85 ? '#e74c3c' : cl.utilPct > 70 ? '#f39c12' : '#2ecc71', minWidth: '28px', textAlign: 'right' }}>{cl.utilPct}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {clientRows.length === 0 && (
                        <tr><td colSpan={5} style={{ padding: '16px', textAlign: 'center', color: '#bbb', fontSize: '11px' }}>No client data for current filters</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── DRILL-DOWN (hidden by default) ── */}
              <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                <button onClick={() => setDrillOpen(drillOpen ? null : 'details')} style={{ padding: '6px 28px', borderRadius: '6px', fontSize: '10px', fontWeight: 600, cursor: 'pointer', background: drillOpen ? '#e67e22' : '#fff', color: drillOpen ? '#fff' : '#e67e22', border: '1px solid #e67e22' }}>
                  {drillOpen ? 'Hide Full Details' : 'View Full Details'} {drillOpen ? '\u25B2' : '\u25BC'}
                </button>
              </div>
              {drillOpen && (
                <div style={{ background: '#fff', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    {/* Per Warehouse Full */}
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#555', marginBottom: '8px' }}>Per Warehouse (All Sources)</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                        <thead><tr style={{ borderBottom: '2px solid #eee' }}>
                          {['Site', 'Rack', 'Blocked', 'Virtual', 'Floor', 'Manual', 'Total Used', 'Cap', '%'].map((h, hi) => (
                            <th key={hi} style={{ padding: '4px 5px', textAlign: hi > 0 ? 'right' : 'left', fontWeight: 700, color: '#999', fontSize: '8px', textTransform: 'uppercase' }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {dc.warehouseRows.map((w: any, wi: number) => {
                            const wp = w.tCap > 0 ? Math.round((w.tUsed / w.tCap) * 100) : 0;
                            return (
                              <tr key={wi} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                <td style={{ padding: '4px 5px', fontWeight: 600, color: '#2c2c2c' }}>{getWarehouseDisplayName(w.warehouse)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right', color: '#6366f1' }}>{formatNumber(w.rackUsed)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right', color: '#f59e0b' }}>{formatNumber(w.blockedCap)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right', color: '#0ea5e9' }}>{formatNumber(w.virtualUsed)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right', color: '#10b981' }}>{formatNumber(w.floorPallets)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right', color: '#a855f7' }}>{formatNumber(w.manualPallets)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right', fontWeight: 700, color: '#e67e22' }}>{formatNumber(w.tUsed)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right', color: '#555' }}>{formatNumber(w.tCap)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right' }}>
                                  <span style={{ fontWeight: 700, color: wp > 85 ? '#e74c3c' : wp > 70 ? '#f39c12' : '#2ecc71' }}>{wp}%</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* Per Chamber Full */}
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#555', marginBottom: '8px' }}>Per Chamber (Rack Only)</div>
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                          <thead><tr style={{ borderBottom: '2px solid #eee' }}>
                            {['Chamber', 'Site', 'Cap', 'Used', 'Sellable', 'Blocked', '%'].map((h, hi) => (
                              <th key={hi} style={{ padding: '4px 5px', textAlign: hi > 1 ? 'right' : 'left', fontWeight: 700, color: '#999', fontSize: '8px', textTransform: 'uppercase' }}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {chambers.map((c: any, ci: number) => (
                              <tr key={ci} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                <td style={{ padding: '4px 5px', fontWeight: 600 }}>{c.chamber}</td>
                                <td style={{ padding: '4px 5px', color: '#888', fontSize: '8px' }}>{getWarehouseDisplayName(c.wh)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right' }}>{formatNumber(c.cap)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right', color: '#e67e22', fontWeight: 600 }}>{formatNumber(c.used)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right', color: '#2ecc71', fontWeight: 600 }}>{formatNumber(Math.max(0, (c.sellable || c.cap) - c.used))}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right', color: '#e74c3c' }}>{formatNumber(c.blockedCap || 0)}</td>
                                <td style={{ padding: '4px 5px', textAlign: 'right' }}>
                                  <span style={{ fontWeight: 700, color: c.utilPct > 85 ? '#e74c3c' : c.utilPct > 70 ? '#f39c12' : '#2ecc71' }}>{c.utilPct}%</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* ═══ RIGHT FILTER PANEL (reference: Month, Year, Department, Location, Space Type) ═══ */}
            <div style={{ width: '190px', background: '#fff', borderLeft: '1px solid #e0e0e0', padding: '14px', flexShrink: 0, overflow: 'auto' }}>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '9px', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Site</div>
                <select value={dashboardWarehouse} onChange={(e) => setDashboardWarehouse(e.target.value)} style={selS}>
                  <option value="All">All Hala Sites</option>
                  {oc.allWarehouses.map((w: string) => <option key={w} value={w}>{getWarehouseDisplayName(w)}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '9px', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Chamber</div>
                <select value={ocChamber} onChange={(e) => setOcChamber(e.target.value)} style={selS}>
                  <option value="All">All Chambers</option>
                  {oc.allChambers.map((c: string) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '9px', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Pallet Type</div>
                <select value={ocPalletType} onChange={(e) => setOcPalletType(e.target.value)} style={selS}>
                  <option value="All">All Types</option>
                  {oc.allPalletTypes.map((p: string) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '9px', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Client</div>
                <select value={ocClient} onChange={(e) => setOcClient(e.target.value)} style={selS}>
                  <option value="All">All Clients</option>
                  {oc.allClients.map((cl: string) => <option key={cl} value={cl}>{cl}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: '#666', cursor: 'pointer' }}>
                  <input type="checkbox" checked={ocExcludeBlocked} onChange={(e) => setOcExcludeBlocked(e.target.checked)} />
                  <span>Exclude Blocked</span>
                </label>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: '#666', cursor: 'pointer' }}>
                  <input type="checkbox" checked={ocExcludeFixedSpace} onChange={(e) => setOcExcludeFixedSpace(e.target.checked)} />
                  <span>Exclude Fixed Space</span>
                </label>
              </div>
              {/* Quick summary in filter panel */}
              <div style={{ borderTop: '1px solid #eee', paddingTop: '10px' }}>
                <div style={{ fontSize: '9px', fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: '6px' }}>Summary</div>
                {[
                  { label: 'Capacity', value: formatNumber(grandCap) },
                  { label: 'Total Used', value: formatNumber(grandUsed), color: '#e67e22' },
                  { label: 'Sellable', value: formatNumber(sellableSpace), color: '#2ecc71' },
                  { label: '─── Breakdown ───', value: '', color: '#ccc' },
                  { label: 'Rack Used', value: formatNumber(dc.rackUsed), color: '#6366f1' },
                  { label: 'Blocked', value: formatNumber(dc.blockedCap), color: '#e74c3c' },
                  { label: 'Virtual', value: formatNumber(dc.virtualUsed), color: '#0ea5e9' },
                  { label: 'Floor', value: formatNumber(dc.floorPallets), color: '#10b981' },
                  { label: 'Manual', value: formatNumber(dc.manualPallets), color: '#a855f7' },
                  { label: 'Commit Gap', value: formatNumber(dc.custShortfall), color: '#9b59b6' },
                ].map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '10px' }}>
                    <span style={{ color: '#888' }}>{s.label}</span>
                    <span style={{ fontWeight: 700, color: s.color || '#2c2c2c' }}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
        );
      })()}

      {activeTab === 'customers' && (
        <div className="ed-tab-content">
          {/* Filter Chips - Modern Pill Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            flexWrap: 'wrap'
          }}>
            {['All', 'Locations', 'Virtual', 'Floor/Bulk', 'Manual'].map((type) => (
              <button
                key={type}
                onClick={() => {
                  setStorageTypeFilter(type);
                  setCustomerPage(0);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: storageTypeFilter === type ? 'none' : '1px solid #e5e7eb',
                  background: storageTypeFilter === type 
                    ? type === 'All' ? '#6366f1' : type === 'Locations' ? '#3b82f6' : type === 'Virtual' ? '#00b4d8' : type === 'Floor/Bulk' ? '#10b981' : '#f59e0b'
                    : '#ffffff',
                  color: storageTypeFilter === type ? '#ffffff' : '#6b7280',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: storageTypeFilter === type ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                }}
              >
                {type}
              </button>
            ))}
          </div>

          <div className="ed-customer-section">
            {storageTypeFilter === 'Virtual' ? (() => {
              const filteredVirtualWH = dashboardWarehouse === 'All'
                ? virtualWarehouseData
                : (virtualWarehouseData || []).filter((v: any) => v.WAREHOUSE === dashboardWarehouse);
              const fvBins = filteredVirtualWH.reduce((s: number, v: any) => s + (v.TOTAL_BINS || 0), 0);
              const fvSegments = filteredVirtualWH.reduce((s: number, v: any) => s + (v.TOTAL_SEGMENTS || 0), 0);
              const fvUsed = filteredVirtualWH.reduce((s: number, v: any) => s + (v.USED_SEGMENTS || 0), 0);
              const fvFree = fvSegments - fvUsed;
              const fvUtil = fvSegments > 0 ? Math.round((fvUsed / fvSegments) * 100 * 100) / 100 : 0;
              return (
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1f2937', margin: '0 0 20px 0' }}>
                  Virtual Storage Summary{dashboardWarehouse !== 'All' ? ` - ${getWarehouseDisplayName(dashboardWarehouse)}` : ''}
                </h3>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>Virtual data is managed at warehouse level, not per customer</p>

                {(virtualStats || filteredVirtualWH.length > 0) && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ background: 'linear-gradient(135deg, #00b4d8 0%, #0891b2 100%)', borderRadius: '10px', padding: '20px', color: '#ffffff' }}>
                      <div style={{ fontSize: '12px', opacity: 0.9, fontWeight: 600, marginBottom: '8px' }}>Virtual Bins</div>
                      <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatNumber(fvBins)}</div>
                    </div>
                    <div style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', borderRadius: '10px', padding: '20px', color: '#ffffff' }}>
                      <div style={{ fontSize: '12px', opacity: 0.9, fontWeight: 600, marginBottom: '8px' }}>Total Segments</div>
                      <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatNumber(fvSegments)}</div>
                    </div>
                    <div style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', borderRadius: '10px', padding: '20px', color: '#ffffff' }}>
                      <div style={{ fontSize: '12px', opacity: 0.9, fontWeight: 600, marginBottom: '8px' }}>Used Segments</div>
                      <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatNumber(fvUsed)}</div>
                    </div>
                    <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '10px', padding: '20px', color: '#ffffff' }}>
                      <div style={{ fontSize: '12px', opacity: 0.9, fontWeight: 600, marginBottom: '8px' }}>Free Segments</div>
                      <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatNumber(fvFree)}</div>
                    </div>
                    <div style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', borderRadius: '10px', padding: '20px', color: '#ffffff' }}>
                      <div style={{ fontSize: '12px', opacity: 0.9, fontWeight: 600, marginBottom: '8px' }}>Utilization</div>
                      <div style={{ fontSize: '24px', fontWeight: 700 }}>{fvUtil}%</div>
                    </div>
                    <div style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', borderRadius: '10px', padding: '20px', color: '#ffffff' }}>
                      <div style={{ fontSize: '12px', opacity: 0.9, fontWeight: 600, marginBottom: '8px' }}>Warehouses</div>
                      <div style={{ fontSize: '24px', fontWeight: 700 }}>{dashboardWarehouse === 'All' ? formatNumber(virtualStats?.TOTAL_WAREHOUSES || 0) : (fvBins > 0 ? '1' : '0')}</div>
                    </div>
                  </div>
                )}

                {filteredVirtualWH.length > 0 && (
                  <div style={{
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '24px',
                    marginBottom: '24px'
                  }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#1f2937', marginBottom: '16px' }}>Virtual Warehouse Details</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
                      {filteredVirtualWH.slice(0, 20).map((row, idx) => (
                        <div key={idx} style={{
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          padding: '16px'
                        }}>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: '#1f2937', marginBottom: '12px' }}>
                            {getWarehouseDisplayName(row.WAREHOUSE)}
                          </div>
                          <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#6b7280' }}>Bins:</span>
                              <span style={{ fontWeight: 600, color: '#1f2937' }}>{row.TOTAL_BINS || 0}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#6b7280' }}>Segments:</span>
                              <span style={{ fontWeight: 600, color: '#1f2937' }}>{formatNumber(row.TOTAL_SEGMENTS || 0)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#6b7280' }}>Used:</span>
                              <span style={{ fontWeight: 600, color: '#ef4444' }}>{formatNumber(row.USED_SEGMENTS || 0)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#6b7280' }}>Free:</span>
                              <span style={{ fontWeight: 600, color: '#10b981' }}>{formatNumber((row.TOTAL_SEGMENTS || 0) - (row.USED_SEGMENTS || 0))}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              );
            })() : (
              <>
                {/* Search Bar */}
                <div style={{
                  position: 'relative',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setCustomerPage(0);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      fontSize: '14px',
                      background: '#ffffff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                      transition: 'all 0.2s ease'
                    }}
                  />
                  <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 600, marginLeft: '12px' }}>
                    {formatNumber(filteredCustomerData.length)} records
                  </span>
                </div>

                {/* Customer Cards Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  {paginatedCustomerData.map((row, idx) => {
                    const util = row.TOTAL_CAPACITY > 0 ? Math.round((row.USED_PALLETS / row.TOTAL_CAPACITY) * 100) : 0;
                    const storageTypeColor = row.STORAGE_TYPE === 'MANUAL' ? '#f59e0b' : row.STORAGE_TYPE === 'Virtual' ? '#00b4d8' : '#3b82f6';
                    return (
                      <div key={idx} style={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '20px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        {/* Customer Name */}
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#1f2937', marginBottom: '8px' }}>
                          {row.CUSTOMER}
                        </div>

                        {/* Badges */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            background: '#f0f9ff',
                            color: '#0369a1',
                            fontSize: '11px',
                            fontWeight: 600,
                            borderRadius: '12px'
                          }}>
                            {getWarehouseDisplayName(row.WAREHOUSE)}
                          </span>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            background: 'rgba(245, 158, 11, 0.1)',
                            color: storageTypeColor,
                            fontSize: '11px',
                            fontWeight: 600,
                            borderRadius: '12px'
                          }}>
                            {row.STORAGE_TYPE}
                          </span>
                        </div>

                        {/* Metrics */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr',
                          gap: '8px',
                          marginBottom: '16px',
                          fontSize: '12px'
                        }}>
                          <div style={{ background: '#f9fafb', padding: '8px', borderRadius: '6px' }}>
                            <div style={{ color: '#6b7280', fontSize: '10px', marginBottom: '2px' }}>Used</div>
                            <div style={{ fontWeight: 700, color: '#1f2937' }}>{formatNumber(row.USED_PALLETS)}</div>
                          </div>
                          <div style={{ background: '#f9fafb', padding: '8px', borderRadius: '6px' }}>
                            <div style={{ color: '#6b7280', fontSize: '10px', marginBottom: '2px' }}>Capacity</div>
                            <div style={{ fontWeight: 700, color: '#1f2937' }}>{formatNumber(row.TOTAL_CAPACITY)}</div>
                          </div>
                          <div style={{ background: '#f9fafb', padding: '8px', borderRadius: '6px' }}>
                            <div style={{ color: '#6b7280', fontSize: '10px', marginBottom: '2px' }}>Available</div>
                            <div style={{ fontWeight: 700, color: '#10b981' }}>{formatNumber(row.AVAILABLE_CAPACITY)}</div>
                          </div>
                        </div>

                        {/* Utilization Bar */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px' }}>
                            <span style={{ color: '#6b7280' }}>Utilization</span>
                            <span style={{ fontWeight: 700, color: util > 80 ? '#ef4444' : util > 60 ? '#f59e0b' : '#10b981' }}>
                              {util}%
                            </span>
                          </div>
                          <div style={{ height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{
                              height: '100%',
                              background: util > 80 ? '#ef4444' : util > 60 ? '#f59e0b' : '#10b981',
                              width: `${util}%`,
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>

                        {/* Location */}
                        <div style={{ fontSize: '12px', color: '#6b7280', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                          Location: <strong>{row.STORAGE_LOCATION}</strong>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '12px',
                  marginTop: '24px'
                }}>
                  <button
                    onClick={() => setCustomerPage(Math.max(0, customerPage - 1))}
                    disabled={customerPage === 0}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: customerPage === 0 ? '#f3f4f6' : '#ffffff',
                      color: customerPage === 0 ? '#d1d5db' : '#6b7280',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: customerPage === 0 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280', padding: '8px 12px' }}>
                    Page {customerPage + 1} of {Math.ceil(sortedCustomerData.length / 50) || 1}
                  </span>
                  <button
                    onClick={() => setCustomerPage(Math.min(Math.ceil(sortedCustomerData.length / 50) - 1, customerPage + 1))}
                    disabled={customerPage >= Math.ceil(sortedCustomerData.length / 50) - 1}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: customerPage >= Math.ceil(sortedCustomerData.length / 50) - 1 ? '#f3f4f6' : '#ffffff',
                      color: customerPage >= Math.ceil(sortedCustomerData.length / 50) - 1 ? '#d1d5db' : '#6b7280',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: customerPage >= Math.ceil(sortedCustomerData.length / 50) - 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}


      {activeTab === 'warehouses' && (
        <div className="ed-tab-content">
          {/* Warehouse Intelligence Header */}
          <div style={{
            background: 'linear-gradient(135deg, #1e1e2f 0%, #2d2d44 100%)',
            padding: '24px 32px',
            borderRadius: '12px',
            marginBottom: '28px',
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: 700,
              color: '#ffffff',
              margin: 0,
              letterSpacing: '-0.5px',
            }}>
              Warehouse Intelligence
            </h2>
          </div>

          {/* Warehouse Cards Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '20px',
            marginBottom: '40px',
          }}>
            {warehouseCards.map((wh, idx) => {
              const warehouseColor = WAREHOUSE_COLOR_MAP[wh.warehouse] || '#6366f1';
              const utilPct = wh.util_percent || 0;

              return (
                <div key={idx} style={{
                  background: '#ffffff',
                  borderRadius: '14px',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                }}>
                  {/* Colored Top Border */}
                  <div style={{
                    height: '6px',
                    background: `linear-gradient(90deg, ${warehouseColor} 0%, ${warehouseColor}dd 100%)`,
                  }} />

                  {/* Card Content */}
                  <div style={{ padding: '20px' }}>
                    {/* Warehouse Name */}
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#1a1a1a',
                      margin: '0 0 16px 0',
                      letterSpacing: '-0.3px',
                    }}>
                      {getWarehouseDisplayName(wh.warehouse)}
                    </h3>

                    {/* Mini Circular Progress Ring */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: '20px',
                    }}>
                      <svg width="80" height="80" style={{ transform: 'rotate(-90deg)' }}>
                        <circle
                          cx="40"
                          cy="40"
                          r="35"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <circle
                          cx="40"
                          cy="40"
                          r="35"
                          fill="none"
                          stroke={warehouseColor}
                          strokeWidth="3"
                          strokeDasharray={`${2 * Math.PI * 35 * (utilPct / 100)} ${2 * Math.PI * 35}`}
                          strokeLinecap="round"
                        />
                        <text
                          x="40"
                          y="45"
                          textAnchor="middle"
                          fontSize="18"
                          fontWeight="700"
                          fill={warehouseColor}
                          style={{ transform: 'rotate(90deg)', transformOrigin: '40px 40px' }}
                        >
                          {Math.round(utilPct)}%
                        </text>
                      </svg>
                    </div>

                    {/* 4 Mini Metric Tiles */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px',
                      marginBottom: '16px',
                    }}>
                      {[
                        { label: 'Capacity', value: formatNumber(wh.total_capacity) },
                        { label: 'Used', value: formatNumber(wh.used_pallets) },
                        { label: 'Free', value: formatNumber(wh.free_pallets) },
                        { label: 'Areas', value: wh.area_count.toString() },
                      ].map((metric, i) => (
                        <div key={i} style={{
                          background: '#f9fafb',
                          padding: '12px',
                          borderRadius: '8px',
                          textAlign: 'center',
                          border: '1px solid #e5e7eb',
                        }}>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#6b7280',
                            marginBottom: '4px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}>
                            {metric.label}
                          </div>
                          <div style={{
                            fontSize: '15px',
                            fontWeight: 700,
                            color: '#1a1a1a',
                          }}>
                            {metric.value}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Horizontal Stacked Bar */}
                    <div style={{
                      display: 'flex',
                      height: '8px',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      background: '#e5e7eb',
                      marginTop: '12px',
                    }}>
                      <div style={{
                        width: `${utilPct}%`,
                        background: warehouseColor,
                        transition: 'width 0.4s ease',
                      }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Storage Area Performance Section */}
          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            padding: '24px',
          }}>
            {/* Section Header with Left Border Accent */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '2px solid #f0f0f0',
            }}>
              <div style={{
                width: '4px',
                height: '24px',
                background: 'linear-gradient(180deg, #6366f1 0%, #9c27b0 100%)',
                borderRadius: '2px',
                marginRight: '12px',
              }} />
              <h3 style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#1a1a1a',
                margin: 0,
              }}>
                Storage Area Performance
              </h3>
            </div>

            {/* Area Performance Bars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {areaAnalysis.map((area, idx) => {
                const areaUtilPct = area.util_percent || 0;
                const areaColor = CHART_COLORS[idx % CHART_COLORS.length];

                return (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                  }}>
                    {/* Area Name */}
                    <div style={{
                      minWidth: '140px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#1a1a1a',
                    }}>
                      {area.area}
                    </div>

                    {/* Horizontal Stacked Bar */}
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      height: '12px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      background: '#e5e7eb',
                    }}>
                      <div style={{
                        width: `${areaUtilPct}%`,
                        background: areaColor,
                        transition: 'width 0.4s ease',
                      }} />
                    </div>

                    {/* Utilization % */}
                    <div style={{
                      minWidth: '45px',
                      textAlign: 'right',
                      fontSize: '14px',
                      fontWeight: 700,
                      color: areaColor,
                    }}>
                      {Math.round(areaUtilPct)}%
                    </div>

                    {/* Badges for Bins and Avg Levels */}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      minWidth: '160px',
                    }}>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#ffffff',
                        background: '#6b7280',
                        padding: '4px 10px',
                        borderRadius: '4px',
                      }}>
                        {area.bins} bins
                      </span>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#ffffff',
                        background: '#9ca3af',
                        padding: '4px 10px',
                        borderRadius: '4px',
                      }}>
                        {area.avg_levels} levels
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="ed-tab-content">
          {/* Advanced Analytics Header */}
          <div style={{
            background: 'linear-gradient(135deg, #1e1e2f 0%, #2d2d44 100%)',
            padding: '24px 32px',
            borderRadius: '12px',
            marginBottom: '28px',
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: 700,
              color: '#ffffff',
              margin: 0,
              letterSpacing: '-0.5px',
            }}>
              Advanced Analytics
            </h2>
          </div>

          {/* ROW 1: Level Distribution Chart (Left) & Segment Donut (Right) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px',
            marginBottom: '28px',
          }}>
            {/* Level Distribution - Vertical Bar Chart */}
            {levelDistribution.length > 0 && (
              <div style={{
                background: '#ffffff',
                borderRadius: '14px',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                padding: '24px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid #f0f0f0',
                }}>
                  <div style={{
                    width: '4px',
                    height: '20px',
                    background: '#6366f1',
                    borderRadius: '2px',
                    marginRight: '10px',
                  }} />
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#1a1a1a',
                    margin: 0,
                  }}>
                    Level Distribution
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={levelDistribution} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="level" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value) => formatNumber(Number(value))}
                      contentStyle={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                    <Bar dataKey="bins" radius={[8, 8, 0, 0]} fill="#6366f1" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Segment Donut Chart */}
            {levelDistribution.length > 0 && (
              <div style={{
                background: '#ffffff',
                borderRadius: '14px',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid #f0f0f0',
                }}>
                  <div style={{
                    width: '4px',
                    height: '20px',
                    background: '#9c27b0',
                    borderRadius: '2px',
                    marginRight: '10px',
                  }} />
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#1a1a1a',
                    margin: 0,
                  }}>
                    Segment Distribution
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={levelDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="used_segments"
                      nameKey="level"
                      label={({ level, percent }: any) => `L${level}: ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {levelDistribution.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatNumber(Number(value))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ROW 2: Top 10 Customers - Horizontal Bar Chart */}
          {topCustomers.length > 0 && (
            <div style={{
              background: '#ffffff',
              borderRadius: '14px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
              padding: '24px',
              marginBottom: '28px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '20px',
                paddingBottom: '12px',
                borderBottom: '2px solid #f0f0f0',
              }}>
                <div style={{
                  width: '4px',
                  height: '20px',
                  background: 'linear-gradient(180deg, #ff3b30 0%, #ff9100 100%)',
                  borderRadius: '2px',
                  marginRight: '10px',
                }} />
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  margin: 0,
                }}>
                  Top 10 Customers by Pallet Volume
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={topCustomers}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" width={145} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => formatNumber(value as number)} />
                  <Bar dataKey="used_pallets" radius={[0, 8, 8, 0]} fill="#ff3b30">
                    {topCustomers.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ROW 3: Shelf Size Distribution (Left: Donut) & Shelf Horizontal Bars (Right) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px',
            marginBottom: '28px',
          }}>
            {/* Shelf Size Donut Chart */}
            {shelfDistribution.length > 0 && (
              <div style={{
                background: '#ffffff',
                borderRadius: '14px',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid #f0f0f0',
                }}>
                  <div style={{
                    width: '4px',
                    height: '20px',
                    background: '#00b4d8',
                    borderRadius: '2px',
                    marginRight: '10px',
                  }} />
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#1a1a1a',
                    margin: 0,
                  }}>
                    Shelf Sizes by Volume
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={shelfDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      dataKey="bins"
                      nameKey="shelf_size"
                      label={({ shelf_size, percent }: any) => `${shelf_size}: ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {shelfDistribution.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatNumber(Number(value))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Shelf Sizes Horizontal Bars */}
            {shelfDistribution.length > 0 && (
              <div style={{
                background: '#ffffff',
                borderRadius: '14px',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                padding: '24px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid #f0f0f0',
                }}>
                  <div style={{
                    width: '4px',
                    height: '20px',
                    background: '#34c759',
                    borderRadius: '2px',
                    marginRight: '10px',
                  }} />
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#1a1a1a',
                    margin: 0,
                  }}>
                    Shelf Utilization
                  </h3>
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}>
                  {shelfDistribution.map((shelf, idx) => {
                    const shelfUtilPct = shelf.util_percent || 0;
                    const shelfColor = CHART_COLORS[idx % CHART_COLORS.length];

                    return (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}>
                        <div style={{
                          minWidth: '70px',
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#1a1a1a',
                        }}>
                          {shelf.shelf_size}
                        </div>
                        <div style={{
                          flex: 1,
                          display: 'flex',
                          height: '10px',
                          borderRadius: '5px',
                          overflow: 'hidden',
                          background: '#e5e7eb',
                        }}>
                          <div style={{
                            width: `${shelfUtilPct}%`,
                            background: shelfColor,
                            transition: 'width 0.4s ease',
                          }} />
                        </div>
                        <div style={{
                          minWidth: '40px',
                          textAlign: 'right',
                          fontSize: '13px',
                          fontWeight: 700,
                          color: shelfColor,
                        }}>
                          {Math.round(shelfUtilPct)}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ROW 4: Segment Capacity Trend - Full Width Gradient Area Chart */}
          {segmentCapacityTrend.length > 0 && (
            <div style={{
              background: '#ffffff',
              borderRadius: '14px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
              padding: '24px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '20px',
                paddingBottom: '12px',
                borderBottom: '2px solid #f0f0f0',
              }}>
                <div style={{
                  width: '4px',
                  height: '20px',
                  background: 'linear-gradient(90deg, #6366f1 0%, #00b4d8 50%, #34c759 100%)',
                  borderRadius: '2px',
                  marginRight: '10px',
                }} />
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  margin: 0,
                }}>
                  Segment Capacity Trend Over Time
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={segmentCapacityTrend} margin={{ top: 10, right: 30, left: 0, bottom: 40 }}>
                  <defs>
                    <linearGradient id="colorLocations" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="colorVirtual" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00b4d8" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#00b4d8" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="colorFloor" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#34c759" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#34c759" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="colorManual" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ff9100" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#ff9100" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value) => formatNumber(value as number)}
                    contentStyle={{
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="Locations"
                    stackId="1"
                    stroke="#6366f1"
                    fillOpacity={1}
                    fill="url(#colorLocations)"
                    isAnimationActive={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="Virtual"
                    stackId="1"
                    stroke="#00b4d8"
                    fillOpacity={1}
                    fill="url(#colorVirtual)"
                    isAnimationActive={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="Floor"
                    stackId="1"
                    stroke="#34c759"
                    fillOpacity={1}
                    fill="url(#colorFloor)"
                    isAnimationActive={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="Manual"
                    stackId="1"
                    stroke="#ff9100"
                    fillOpacity={1}
                    fill="url(#colorManual)"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {activeTab === 'dynamic' && (() => {
        const tCap = dynamicCapacityStats.totalCapacity;
        const tUsed = dynamicCapacityStats.totalUsed;
        const tFree = dynamicCapacityStats.totalFree;
        const utilPct = tCap > 0 ? Math.round((tUsed / tCap) * 100 * 10) / 10 : 0;
        const freePct = tCap > 0 ? Math.round((tFree / tCap) * 100 * 10) / 10 : 0;
        const rackFree = Math.max(0, dynamicCapacityStats.rackCap - dynamicCapacityStats.rackUsed - dynamicCapacityStats.blockedCap);
        const manualFree = Math.max(0, dynamicCapacityStats.manualCap - dynamicCapacityStats.manualPallets);

        // Chart data
        const storageDonutData = [
          { name: 'Rack', value: dynamicCapacityStats.rackUsed, fill: '#6366f1' },
          { name: 'Blocked', value: dynamicCapacityStats.blockedCap, fill: '#f59e0b' },
          { name: 'Virtual', value: dynamicCapacityStats.virtualUsed, fill: '#0ea5e9' },
          { name: 'Floor', value: dynamicCapacityStats.floorPallets, fill: '#10b981' },
          { name: 'Manual', value: dynamicCapacityStats.manualPallets, fill: '#a855f7' },
          { name: 'Available', value: tFree, fill: '#d1d5db' },
        ].filter(d => d.value > 0);

        const whBarData = dynamicCapacityStats.warehouseRows.map((row: any) => ({
          name: getWarehouseDisplayName(row.warehouse).substring(0, 14),
          warehouse: row.warehouse,
          Rack: row.rackUsed,
          Blocked: row.blockedCap,
          Virtual: row.virtualUsed,
          Floor: row.floorPallets,
          Manual: row.manualPallets,
          Available: Math.max(0, row.tCap - row.tUsed - row.custShortfall),
          capacity: row.tCap,
        }));

        const whUtilData = dynamicCapacityStats.warehouseRows
          .map((row: any) => {
            const pct = row.tCap > 0 ? Math.round(((row.tUsed + row.custShortfall) / row.tCap) * 100 * 10) / 10 : 0;
            return {
              name: getWarehouseDisplayName(row.warehouse),
              warehouse: row.warehouse,
              utilization: Math.min(pct, 100),
              remaining: Math.max(0, 100 - pct),
              freePallets: Math.max(0, row.tCap - row.tUsed - row.custShortfall),
              capacity: row.tCap,
              pct,
            };
          })
          .sort((a: any, b: any) => b.pct - a.pct);

        // Styles
        const cardStyle = { background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' };
        const sectionTitle = (text: string) => (
          <h3 style={{ margin: '0 0 14px', fontSize: '12px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '1px' }}>{text}</h3>
        );

        return (
        <div style={{ display: 'flex', gap: '0', minHeight: '85vh', background: '#f8f9fb', margin: '-20px', borderRadius: '12px', overflow: 'hidden' }}>

          {/* ══════ LEFT SIDEBAR ══════ */}
          <div style={{ width: '200px', background: '#1a1a2e', padding: '24px 0', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <div style={{ padding: '0 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff', letterSpacing: '-0.3px' }}>Capacity</div>
              <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>Dynamic Analysis</div>
            </div>
            {[
              { icon: '📊', label: 'Overview', active: true },
              { icon: '🏭', label: 'Warehouses', active: false },
              { icon: '📦', label: 'Storage Types', active: false },
              { icon: '📋', label: 'Commitments', active: false },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 20px', cursor: 'pointer',
                background: item.active ? 'rgba(99,102,241,0.15)' : 'transparent',
                borderLeft: item.active ? '3px solid #6366f1' : '3px solid transparent',
                color: item.active ? '#a5b4fc' : '#9ca3af',
                fontSize: '13px', fontWeight: item.active ? 600 : 400,
                transition: 'all 0.15s',
              }}>
                <span style={{ fontSize: '16px' }}>{item.icon}</span>
                {item.label}
              </div>
            ))}

            {/* Sidebar Quick Stats */}
            <div style={{ marginTop: 'auto', padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Quick Stats</div>
              {[
                { label: 'Rack SM', value: `${formatNumber(dynamicCapacityStats.rackSmallBins)}`, color: '#818cf8' },
                { label: 'Rack LG', value: `${formatNumber(dynamicCapacityStats.rackLargeBins)}`, color: '#fbbf24' },
                { label: 'Blocked', value: `${formatNumber(dynamicCapacityStats.blockedBins)}`, color: '#f87171' },
                { label: 'Virtual', value: `${formatNumber(dynamicCapacityStats.virtualBins)}`, color: '#38bdf8' },
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', fontSize: '11px' }}>
                  <span style={{ color: '#9ca3af' }}>{s.label}</span>
                  <span style={{ color: s.color, fontWeight: 600 }}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ══════ MAIN CONTENT ══════ */}
          <div style={{ flex: 1, padding: '24px 28px', overflow: 'auto' }}>

            {/* ── TOP HEADER ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>Dynamic Capacity Dashboard</h2>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>Real-time pallet-level warehouse capacity analysis</p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={onRefresh} style={{ padding: '7px 16px', borderRadius: '8px', background: '#6366f1', color: '#fff', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Refresh</button>
                <button onClick={() => window.print()} style={{ padding: '7px 16px', borderRadius: '8px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Print</button>
              </div>
            </div>

            {/* ── KPI STRIP (like reference: big numbers in a row) ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '22px' }}>
              {[
                { label: 'Total Capacity', value: formatNumber(tCap), color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
                { label: 'Used Pallets', value: formatNumber(tUsed), color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
                { label: 'Available Space', value: formatNumber(tFree), color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
                { label: 'Utilization', value: `${utilPct}%`, color: utilPct > 85 ? '#dc2626' : utilPct > 70 ? '#d97706' : '#16a34a', bg: utilPct > 85 ? '#fef2f2' : utilPct > 70 ? '#fffbeb' : '#f0fdf4', border: utilPct > 85 ? '#fecaca' : utilPct > 70 ? '#fde68a' : '#bbf7d0' },
                { label: 'Warehouses', value: String(dynamicCapacityStats.warehouseRows.length), color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
              ].map((kpi, i) => (
                <div key={i} style={{ background: kpi.bg, borderRadius: '12px', padding: '16px 18px', border: `1px solid ${kpi.border}`, textAlign: 'center' as const }}>
                  <div style={{ fontSize: '28px', fontWeight: 800, color: kpi.color, lineHeight: 1 }}>{kpi.value}</div>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{kpi.label}</div>
                </div>
              ))}
            </div>

            {/* ── ROW 2: GAUGE + DONUT + UTILIZATION BY WAREHOUSE ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr 1fr', gap: '16px', marginBottom: '22px' }}>

              {/* Utilization Rate Gauge (speedometer style) */}
              <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                {sectionTitle('Utilization Rate (%)')}
                <div style={{ position: 'relative', width: '180px', height: '110px', overflow: 'hidden' }}>
                  <svg viewBox="0 0 200 120" style={{ width: '100%', height: '100%' }}>
                    {/* Background arc */}
                    <path d="M 20 110 A 80 80 0 0 1 180 110" fill="none" stroke="#e5e7eb" strokeWidth="16" strokeLinecap="round" />
                    {/* Colored arc — green to yellow to red */}
                    <defs>
                      <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#22c55e" />
                        <stop offset="50%" stopColor="#eab308" />
                        <stop offset="100%" stopColor="#ef4444" />
                      </linearGradient>
                    </defs>
                    <path d="M 20 110 A 80 80 0 0 1 180 110" fill="none" stroke="url(#gaugeGrad)" strokeWidth="16" strokeLinecap="round"
                      strokeDasharray={`${Math.min(Number(utilPct), 100) / 100 * 251.2} 251.2`}
                      style={{ transition: 'stroke-dasharray 1s ease' }}
                    />
                    {/* Needle */}
                    {(() => {
                      const angle = -180 + (Math.min(Number(utilPct), 100) / 100) * 180;
                      const rad = angle * Math.PI / 180;
                      const nx = 100 + 60 * Math.cos(rad);
                      const ny = 110 + 60 * Math.sin(rad);
                      return <line x1="100" y1="110" x2={nx} y2={ny} stroke="#1f2937" strokeWidth="2.5" strokeLinecap="round" />;
                    })()}
                    <circle cx="100" cy="110" r="6" fill="#1f2937" />
                    <circle cx="100" cy="110" r="3" fill="#fff" />
                  </svg>
                </div>
                <div style={{ fontSize: '32px', fontWeight: 800, color: utilPct > 85 ? '#dc2626' : utilPct > 70 ? '#d97706' : '#16a34a', marginTop: '4px' }}>{utilPct}%</div>
                <div style={{ display: 'flex', gap: '20px', marginTop: '10px', fontSize: '11px' }}>
                  <span style={{ color: '#6b7280' }}>Occupied: <strong style={{ color: '#dc2626' }}>{formatNumber(tUsed)}</strong></span>
                  <span style={{ color: '#6b7280' }}>Available: <strong style={{ color: '#16a34a' }}>{formatNumber(tFree)}</strong></span>
                </div>
              </div>

              {/* Usage Status Distribution (Donut) */}
              <div style={cardStyle}>
                {sectionTitle('Usage Status Distribution')}
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={storageDonutData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" nameKey="name" isAnimationActive={false}
                      label={({ name, percent }: any) => `${name}, ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                    >
                      {storageDonutData.map((d: any, i: number) => <Cell key={i} fill={d.fill} />)}
                    </Pie>
                    <Tooltip formatter={(v: any) => formatNumber(Number(v))} contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Utilization Rate (%) by Warehouse — horizontal bars like reference */}
              <div style={cardStyle}>
                {sectionTitle('Utilization Rate (%) by Warehouse')}
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={whUtilData} layout="vertical" margin={{ left: 0, right: 20, top: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v: number) => `${v}%`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151', fontWeight: 500 }} width={90} />
                    <Tooltip
                      formatter={(v: any, name: any, props: any) => {
                        if (name === 'utilization') return [`${props.payload.pct}%`, 'Utilization'];
                        return [`${(100 - props.payload.pct).toFixed(1)}%`, 'Available'];
                      }}
                      contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb' }}
                    />
                    <Bar dataKey="utilization" stackId="a" fill="#6366f1" name="utilization" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="remaining" stackId="a" fill="#e5e7eb" name="available" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ── ROW 3: WAREHOUSE CAPACITY STACKED BAR + AVAILABLE SPACE CARDS ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '16px', marginBottom: '22px' }}>
              {/* Stacked Bar: Full warehouse comparison */}
              <div style={cardStyle}>
                {sectionTitle('Warehouse Capacity Breakdown')}
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={whBarData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6b7280' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v)} />
                    <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} formatter={(v: any, name: any) => [formatNumber(Number(v)), name]} />
                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                    <Bar dataKey="Rack" stackId="a" fill="#6366f1" />
                    <Bar dataKey="Blocked" stackId="a" fill="#f59e0b" />
                    <Bar dataKey="Virtual" stackId="a" fill="#0ea5e9" />
                    <Bar dataKey="Floor" stackId="a" fill="#10b981" />
                    <Bar dataKey="Manual" stackId="a" fill="#a855f7" />
                    <Bar dataKey="Available" stackId="a" fill="#d1fae5" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Available Space Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={cardStyle}>
                  {sectionTitle('Available Space Summary')}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {[
                      { label: 'Rack Free', value: rackFree, total: dynamicCapacityStats.rackCap, color: '#6366f1' },
                      { label: 'Manual Free', value: manualFree, total: dynamicCapacityStats.manualCap, color: '#a855f7' },
                    ].filter(s => s.total > 0).map((s, i) => (
                      <div key={i} style={{ padding: '12px', background: '#f9fafb', borderRadius: '10px', borderLeft: `3px solid ${s.color}` }}>
                        <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>{s.label}</div>
                        <div style={{ fontSize: '22px', fontWeight: 800, color: '#16a34a', marginTop: '2px' }}>{formatNumber(s.value)}</div>
                        <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                          <div style={{ width: `${s.total > 0 ? ((s.total - s.value) / s.total) * 100 : 0}%`, height: '100%', background: s.color, borderRadius: '2px' }} />
                        </div>
                        <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '3px' }}>of {formatNumber(s.total)} total</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Storage type pills */}
                <div style={cardStyle}>
                  {sectionTitle('Storage Breakdown')}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {[
                      { label: 'Rack Used', value: dynamicCapacityStats.rackUsed, color: '#6366f1', pct: tUsed > 0 ? Math.round((dynamicCapacityStats.rackUsed / tUsed) * 100) : 0 },
                      { label: 'Blocked', value: dynamicCapacityStats.blockedCap, color: '#f59e0b', pct: tUsed > 0 ? Math.round((dynamicCapacityStats.blockedCap / tUsed) * 100) : 0 },
                      { label: 'Virtual', value: dynamicCapacityStats.virtualUsed, color: '#0ea5e9', pct: tUsed > 0 ? Math.round((dynamicCapacityStats.virtualUsed / tUsed) * 100) : 0 },
                      { label: 'Floor', value: dynamicCapacityStats.floorPallets, color: '#10b981', pct: tUsed > 0 ? Math.round((dynamicCapacityStats.floorPallets / tUsed) * 100) : 0 },
                      { label: 'Manual', value: dynamicCapacityStats.manualPallets, color: '#a855f7', pct: tUsed > 0 ? Math.round((dynamicCapacityStats.manualPallets / tUsed) * 100) : 0 },
                    ].filter(s => s.value > 0).map((s, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: s.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '11px', color: '#374151', fontWeight: 500, minWidth: '60px' }}>{s.label}</span>
                        <div style={{ flex: 1, height: '8px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${s.pct}%`, height: '100%', background: s.color, borderRadius: '4px', transition: 'width 0.5s' }} />
                        </div>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#374151', minWidth: '50px', textAlign: 'right' as const }}>{formatNumber(s.value)}</span>
                        <span style={{ fontSize: '10px', color: '#9ca3af', minWidth: '30px', textAlign: 'right' as const }}>{s.pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── ROW 4: WAREHOUSE DETAIL CARDS WITH MINI DONUTS ── */}
            <div style={{ marginBottom: '22px' }}>
              {sectionTitle('Warehouse Detail Cards — Click to Drill Down')}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
                {dynamicCapacityStats.warehouseRows.map((row: any, idx: number) => {
                  const usedWS = row.tUsed + row.custShortfall;
                  const freeWS = Math.max(0, row.tCap - usedWS);
                  const pct = row.tCap > 0 ? Math.round((usedWS / row.tCap) * 100 * 10) / 10 : 0;
                  const statusColor = pct > 100 ? '#dc2626' : pct >= 90 ? '#f59e0b' : pct >= 70 ? '#2563eb' : '#16a34a';
                  const whPie = [
                    { name: 'Used', value: usedWS, fill: statusColor },
                    { name: 'Free', value: Math.max(freeWS, 0), fill: '#e5e7eb' },
                  ];
                  return (
                    <div key={idx} onClick={() => setDrillDownWh(row.warehouse)}
                      style={{ ...cardStyle, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', gap: '16px', alignItems: 'center' }}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#6366f1'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                    >
                      {/* Mini pie */}
                      <div style={{ width: '70px', height: '70px', flexShrink: 0, position: 'relative' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={whPie} cx="50%" cy="50%" innerRadius={20} outerRadius={33} dataKey="value" isAnimationActive={false} startAngle={90} endAngle={-270}>
                              {whPie.map((d: any, i: number) => <Cell key={i} fill={d.fill} stroke="none" />)}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '10px', fontWeight: 800, color: statusColor }}>{pct}%</div>
                      </div>
                      {/* Details */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}>{getWarehouseDisplayName(row.warehouse)}</div>
                        <div style={{ display: 'flex', gap: '16px', fontSize: '11px', flexWrap: 'wrap' as const }}>
                          <span><span style={{ color: '#9ca3af' }}>Cap:</span> <strong style={{ color: '#2563eb' }}>{formatNumber(row.tCap)}</strong></span>
                          <span><span style={{ color: '#9ca3af' }}>Used:</span> <strong style={{ color: '#dc2626' }}>{formatNumber(usedWS)}</strong></span>
                          <span><span style={{ color: '#9ca3af' }}>Free:</span> <strong style={{ color: '#16a34a' }}>{formatNumber(freeWS)}</strong></span>
                        </div>
                        {/* Mini breakdown bar */}
                        <div style={{ height: '6px', background: '#f3f4f6', borderRadius: '3px', marginTop: '8px', overflow: 'hidden', display: 'flex' }}>
                          <div style={{ width: `${row.tCap > 0 ? (row.rackUsed / row.tCap) * 100 : 0}%`, background: '#6366f1' }} />
                          <div style={{ width: `${row.tCap > 0 ? (row.blockedCap / row.tCap) * 100 : 0}%`, background: '#f59e0b' }} />
                          <div style={{ width: `${row.tCap > 0 ? (row.virtualUsed / row.tCap) * 100 : 0}%`, background: '#0ea5e9' }} />
                          <div style={{ width: `${row.tCap > 0 ? (row.floorPallets / row.tCap) * 100 : 0}%`, background: '#10b981' }} />
                          <div style={{ width: `${row.tCap > 0 ? (row.manualPallets / row.tCap) * 100 : 0}%`, background: '#a855f7' }} />
                        </div>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' as const }}>
                          {[
                            { l: 'Rack', v: row.rackUsed, c: '#6366f1' },
                            { l: 'Blk', v: row.blockedCap, c: '#f59e0b' },
                            { l: 'Vrt', v: row.virtualUsed, c: '#0ea5e9' },
                            { l: 'Flr', v: row.floorPallets, c: '#10b981' },
                            { l: 'Man', v: row.manualPallets, c: '#a855f7' },
                          ].filter(x => x.v > 0).map((x, xi) => (
                            <span key={xi} style={{ fontSize: '9px', color: x.c, fontWeight: 600 }}>{x.l}:{formatNumber(x.v)}</span>
                          ))}
                          {row.custShortfall > 0 && <span style={{ fontSize: '9px', color: '#dc2626', fontWeight: 700 }}>MIN:+{formatNumber(row.custShortfall)}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          {/* ═══════════════════ CUSTOMER MINIMUM COMMITMENTS ═══════════════════ */}
          {(() => {
            // Use ALL customers across all warehouses (not warehouse-filtered) so cross-warehouse commitments work
            const allCustomers = Array.from(new Set(correctedCustomerStorageData.map((c: any) => c.CUSTOMER).filter(Boolean))).sort();
            const allWarehouses = Array.from(new Set(correctedCustomerStorageData.map((c: any) => c.WAREHOUSE).filter(Boolean))).sort();

            // Use custActualPallets (IBM + Manual merged) for actual balance
            // Supports grouped customers AND grouped warehouses (both comma-separated)
            const getActualForCommitment = (row: CustMinRow): number => {
              const customers = row.customer.split(',').map(c => c.trim()).filter(Boolean);
              const warehouses = row.warehouse === 'All' ? ['All'] : row.warehouse.split(',').map(w => w.trim()).filter(Boolean);
              let total = 0;
              customers.forEach(cust => {
                if (warehouses.includes('All')) {
                  total += (custActualPallets[cust] || 0);
                } else {
                  warehouses.forEach(wh => {
                    total += (custActualPallets[`${cust}__${wh}`] || 0);
                  });
                }
              });
              return total;
            };

            const enriched = custMinRows.map(row => {
              const actual = getActualForCommitment(row);
              const effective = Math.max(row.minPallets, actual);
              const shortfall = Math.max(0, row.minPallets - actual);
              const cTypeInfo = COMMITMENT_TYPES.find(t => t.value === row.commitmentType) || COMMITMENT_TYPES[0];
              return { ...row, actual, effective, shortfall, cTypeInfo };
            });

            const totalMin = enriched.reduce((s, r) => s + r.minPallets, 0);
            const totalActual = enriched.reduce((s, r) => s + r.actual, 0);
            const totalEffective = enriched.reduce((s, r) => s + r.effective, 0);
            const totalShortfall = enriched.reduce((s, r) => s + r.shortfall, 0);

            const inputStyle = { padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', width: '100%', boxSizing: 'border-box' as const };
            const labelStyle = { fontSize: '11px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: 600 as const, textTransform: 'uppercase' as const, letterSpacing: '0.3px' };

            return (
              <div style={{ marginTop: '8px' }}>
                {/* Section Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', padding: '18px 24px', background: 'linear-gradient(135deg, #fafbff 0%, #f0f4ff 100%)', borderRadius: '14px', border: '1px solid #e0e7ff' }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ width: '4px', height: '20px', borderRadius: '2px', background: 'linear-gradient(180deg, #6366f1, #a855f7)', display: 'inline-block' }} />
                      Customer Minimum Commitment
                    </h2>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 12px' }}>
                      Contract-based pallet commitments — shortfall counts as reserved capacity
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '11px', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '4px', background: '#f0fdf4', padding: '4px 10px', borderRadius: '20px', border: '1px solid #bbf7d0' }}>
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a', display: 'inline-block', animation: 'pulse 2s infinite' }} />
                      Azure SQL
                    </span>
                    <button
                      onClick={() => setShowAddForm(!showAddForm)}
                      style={{ padding: '8px 20px', borderRadius: '10px', background: showAddForm ? '#64748b' : 'linear-gradient(135deg, #6366f1, #4f46e5)', color: '#fff', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer', boxShadow: showAddForm ? 'none' : '0 2px 8px rgba(99,102,241,0.3)' }}
                    >
                      {showAddForm ? 'Cancel' : '+ New Commitment'}
                    </button>
                  </div>
                </div>

                {/* Summary KPIs — redesigned with available space focus */}
                {custMinRows.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '18px' }}>
                    <div style={{ background: '#fff', borderRadius: '14px', padding: '16px 18px', border: '1px solid #e2e8f0', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #0ea5e9, #38bdf8)' }} />
                      <div style={{ fontSize: '10px', color: '#0369a1', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>Min. Committed</div>
                      <div style={{ fontSize: '24px', fontWeight: 800, color: '#0c4a6e', marginTop: '4px' }}>{formatNumber(totalMin)}</div>
                      <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>pallet positions</div>
                    </div>
                    <div style={{ background: '#fff', borderRadius: '14px', padding: '16px 18px', border: '1px solid #e2e8f0', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #22c55e, #4ade80)' }} />
                      <div style={{ fontSize: '10px', color: '#166534', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>Actual Stored</div>
                      <div style={{ fontSize: '24px', fontWeight: 800, color: '#14532d', marginTop: '4px' }}>{formatNumber(totalActual)}</div>
                      <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>current pallets</div>
                    </div>
                    <div style={{ background: '#fff', borderRadius: '14px', padding: '16px 18px', border: '1px solid #e2e8f0', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} />
                      <div style={{ fontSize: '10px', color: '#92400e', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>Effective Used</div>
                      <div style={{ fontSize: '24px', fontWeight: 800, color: '#78350f', marginTop: '4px' }}>{formatNumber(totalEffective)}</div>
                      <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>max(min, actual)</div>
                    </div>
                    <div style={{ background: totalShortfall > 0 ? '#fff5f5' : '#fff', borderRadius: '14px', padding: '16px 18px', border: `1px solid ${totalShortfall > 0 ? '#fecaca' : '#e2e8f0'}`, position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: totalShortfall > 0 ? 'linear-gradient(90deg, #ef4444, #f87171)' : 'linear-gradient(90deg, #22c55e, #4ade80)' }} />
                      <div style={{ fontSize: '10px', color: totalShortfall > 0 ? '#991b1b' : '#166534', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>Shortfall</div>
                      <div style={{ fontSize: '24px', fontWeight: 800, color: totalShortfall > 0 ? '#dc2626' : '#16a34a', marginTop: '4px' }}>{totalShortfall > 0 ? `+${formatNumber(totalShortfall)}` : '0'}</div>
                      <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>reserved capacity</div>
                    </div>
                  </div>
                )}

                {/* Executive Commitment Dashboard — visual chart */}
                {enriched.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '16px', marginBottom: '20px' }}>
                    {/* Donut Gauge — Overall Fulfillment */}
                    <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '12px' }}>Overall Fulfillment</div>
                      <div style={{ position: 'relative', width: '160px', height: '160px' }}>
                        <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                          {/* Background ring */}
                          <circle cx="60" cy="60" r="50" fill="none" stroke="#f1f5f9" strokeWidth="12" />
                          {/* Actual stored (green) */}
                          <circle cx="60" cy="60" r="50" fill="none" stroke="#22c55e" strokeWidth="12"
                            strokeDasharray={`${totalMin > 0 ? Math.min(100, (totalActual / totalMin) * 100) * 3.14 : 0} 314`}
                            strokeLinecap="round" />
                          {/* Shortfall (red) — if any */}
                          {totalShortfall > 0 && (
                            <circle cx="60" cy="60" r="50" fill="none" stroke="#ef4444" strokeWidth="12" opacity="0.3"
                              strokeDasharray={`${(totalShortfall / totalMin) * 100 * 3.14} 314`}
                              strokeDashoffset={`${-(totalMin > 0 ? Math.min(100, (totalActual / totalMin) * 100) * 3.14 : 0)}`}
                              strokeLinecap="round" />
                          )}
                        </svg>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                          <div style={{ fontSize: '28px', fontWeight: 800, color: totalShortfall > 0 ? '#dc2626' : '#16a34a' }}>
                            {totalMin > 0 ? Math.round((totalActual / totalMin) * 100) : 100}%
                          </div>
                          <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>fulfilled</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '16px', marginTop: '14px', fontSize: '11px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }} />
                          <span style={{ color: '#64748b' }}>Stored</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', opacity: 0.5 }} />
                          <span style={{ color: '#64748b' }}>Shortfall</span>
                        </div>
                      </div>
                    </div>

                    {/* Per-Customer Commitment vs Actual — horizontal bars */}
                    <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '20px' }}>
                      <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '14px' }}>
                        Commitment vs Actual — by Customer
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '500px', overflowY: 'auto' }}>
                        {enriched.map((row, idx) => {
                          const maxVal = Math.max(row.minPallets, row.actual, 1);
                          const commitPct = (row.minPallets / maxVal) * 100;
                          const actualPct = (row.actual / maxVal) * 100;
                          const isMet = row.actual >= row.minPallets;
                          return (
                            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 90px', alignItems: 'center', gap: '10px' }}>
                              <div style={{ fontSize: '11px', fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.customer}>
                                {row.customer.includes(',') ? `GROUP(${row.customer.split(',').length})` : row.customer}
                              </div>
                              <div style={{ position: 'relative', height: '22px', background: '#f8fafc', borderRadius: '6px', overflow: 'hidden' }}>
                                {/* Commitment baseline (dashed line) */}
                                <div style={{ position: 'absolute', left: `${commitPct}%`, top: 0, bottom: 0, width: '2px', background: '#94a3b8', zIndex: 2, borderLeft: '1px dashed #64748b' }} />
                                {/* Actual fill bar */}
                                <div style={{
                                  width: `${actualPct}%`, height: '100%', borderRadius: '6px',
                                  background: isMet
                                    ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                                    : 'linear-gradient(90deg, #fbbf24, #f59e0b)',
                                  transition: 'width 0.5s ease',
                                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '6px',
                                }} >
                                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                    {formatNumber(row.actual)}
                                  </span>
                                </div>
                              </div>
                              <div style={{ fontSize: '11px', textAlign: 'right' }}>
                                <span style={{ color: isMet ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                                  {isMet ? 'Met' : `-${formatNumber(row.shortfall)}`}
                                </span>
                                <span style={{ color: '#94a3b8', marginLeft: '4px', fontSize: '10px' }}>
                                  / {formatNumber(row.minPallets)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* All customers shown in chart above */}
                    </div>
                  </div>
                )}

                {/* Add Commitment Form */}
                {showAddForm && (
                  <div style={{ background: '#f8fafc', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', marginBottom: '16px' }}>New Customer Commitment <span style={{ fontSize: '11px', fontWeight: 400, color: '#64748b' }}>— select one or group multiple customers</span></div>
                    {/* Customer multi-select with search */}
                    <div style={{ marginBottom: '14px' }}>
                      <label style={labelStyle}>Customer(s) — select multiple to create a group commitment</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          id="cmCustSearch"
                          type="text"
                          placeholder="Search customers..."
                          style={{ ...inputStyle, marginBottom: '4px' }}
                          onInput={(e) => {
                            const search = (e.target as HTMLInputElement).value.toLowerCase();
                            const items = document.querySelectorAll('.cm-cust-item');
                            items.forEach((item: any) => {
                              item.style.display = item.dataset.name?.toLowerCase().includes(search) ? '' : 'none';
                            });
                          }}
                        />
                        <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '4px' }}>
                          {allCustomers.map((c: string) => (
                            <label key={c} className="cm-cust-item" data-name={c} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px', borderRadius: '4px' }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                              <input type="checkbox" className="cm-cust-cb" value={c} style={{ accentColor: '#0ea5e9' }} />
                              <span>{c}</span>
                            </label>
                          ))}
                        </div>
                        <div id="cmSelectedCustomers" style={{ fontSize: '11px', color: '#0369a1', fontWeight: 600, marginTop: '4px', minHeight: '16px' }}></div>
                      </div>
                    </div>
                    {/* Warehouse multi-select */}
                    <div style={{ marginBottom: '14px' }}>
                      <label style={labelStyle}>Warehouse(s) — select one, multiple, or leave empty for All</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#f8fafc' }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#e0f2fe')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '#f8fafc')}>
                          <input type="checkbox" className="cm-wh-cb" value="All" style={{ accentColor: '#6366f1' }}
                            onChange={(e) => {
                              // "All" unchecks everything else; checking a specific WH unchecks "All"
                              if (e.target.checked) {
                                document.querySelectorAll('.cm-wh-cb').forEach((cb: any) => { if (cb.value !== 'All') cb.checked = false; });
                              }
                            }}
                          />
                          <span style={{ fontWeight: 600 }}>All</span>
                        </label>
                        {allWarehouses.map((w: string) => (
                          <label key={w} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#f8fafc' }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#e0f2fe')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = '#f8fafc')}>
                            <input type="checkbox" className="cm-wh-cb" value={w} style={{ accentColor: '#6366f1' }}
                              onChange={() => {
                                // Uncheck "All" when specific WH selected
                                const allCb = document.querySelector('.cm-wh-cb[value="All"]') as HTMLInputElement;
                                if (allCb) allCb.checked = false;
                              }}
                            />
                            <span>{getWarehouseDisplayName(w)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 0.8fr 1fr 1.5fr', gap: '12px', marginBottom: '16px' }}>
                      <div>
                        <label style={labelStyle}>Commitment Type</label>
                        <select id="cmType" style={inputStyle} onChange={(e) => {
                          const ct = COMMITMENT_TYPES.find(t => t.value === e.target.value);
                          const fEl = document.getElementById('cmFactor') as HTMLInputElement;
                          const dfEl = document.getElementById('cmDisplayFactor');
                          if (ct && fEl) fEl.value = String(ct.defaultFactor);
                          if (ct && dfEl) dfEl.textContent = ct.displayFactor;
                          // Auto-recalculate pallets preview
                          const vEl = document.getElementById('cmValue') as HTMLInputElement;
                          const previewEl = document.getElementById('cmPalletPreview');
                          if (ct && vEl && previewEl) {
                            const val = parseFloat(vEl.value) || 0;
                            const pallets = Math.ceil(val * ct.defaultFactor);
                            const isDivide = ct.displayFactor.startsWith('÷');
                            previewEl.textContent = val > 0 ? `= ${pallets.toLocaleString()} pallets${isDivide ? ` (${val.toLocaleString()} ${ct.displayFactor})` : ''}` : '';
                          }
                          // Show hint
                          const hintEl = document.getElementById('cmTypeHint');
                          if (hintEl) hintEl.textContent = ct?.hint || '';
                        }}>
                          {COMMITMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <div id="cmTypeHint" style={{ fontSize: '10px', color: '#0ea5e9', marginTop: '2px', minHeight: '14px' }}></div>
                      </div>
                      <div>
                        <label style={labelStyle}>Value</label>
                        <input id="cmValue" type="number" placeholder="e.g. 10400" min={0} style={inputStyle} onInput={() => {
                          const vEl = document.getElementById('cmValue') as HTMLInputElement;
                          const fEl = document.getElementById('cmFactor') as HTMLInputElement;
                          const previewEl = document.getElementById('cmPalletPreview');
                          const typeEl = document.getElementById('cmType') as HTMLSelectElement;
                          const ct = COMMITMENT_TYPES.find(t => t.value === typeEl?.value);
                          if (vEl && fEl && previewEl) {
                            const val = parseFloat(vEl.value) || 0;
                            const factor = parseFloat(fEl.value) || 1;
                            const pallets = Math.ceil(val * factor);
                            const isDivide = ct?.displayFactor?.startsWith('÷');
                            previewEl.textContent = val > 0 ? `= ${pallets.toLocaleString()} pallets${isDivide ? ` (${val.toLocaleString()} ${ct?.displayFactor})` : ''}` : '';
                          }
                        }} />
                        <div id="cmPalletPreview" style={{ fontSize: '11px', fontWeight: 700, color: '#0369a1', marginTop: '2px', minHeight: '14px' }}></div>
                      </div>
                      <div>
                        <label style={labelStyle}>Conversion</label>
                        <div style={{ ...inputStyle, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#6366f1' }}>
                          <span id="cmDisplayFactor">x1</span>
                        </div>
                        <input id="cmFactor" type="hidden" defaultValue="1" />
                      </div>
                      <div>
                        <label style={labelStyle}>Storage Type</label>
                        <select id="cmStorageType" style={inputStyle}>
                          {STORAGE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Notes</label>
                        <input id="cmNotes" type="text" placeholder="Optional notes" style={inputStyle} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        <strong>Conversion to Pallets:</strong> &nbsp; PP: x1 &nbsp;|&nbsp; <strong>SQM: ÷1.2</strong> (1 pallet = 1.2 SQM) &nbsp;|&nbsp; <strong>SQFT: ÷12.917</strong> &nbsp;|&nbsp; MT: x0.714 &nbsp;|&nbsp; KT: x714.29 &nbsp;|&nbsp; CBM: x0.5 &nbsp;|&nbsp; Bins: x1
                      </div>
                      <button
                        disabled={custMinLoading}
                        onClick={() => {
                          // Gather selected customers from checkboxes
                          const checkboxes = document.querySelectorAll('.cm-cust-cb:checked') as NodeListOf<HTMLInputElement>;
                          const selectedCustomers = Array.from(checkboxes).map(cb => cb.value);
                          const customer = selectedCustomers.join(', ');
                          // Gather selected warehouses from checkboxes
                          const whCheckboxes = document.querySelectorAll('.cm-wh-cb:checked') as NodeListOf<HTMLInputElement>;
                          const selectedWHs = Array.from(whCheckboxes).map(cb => cb.value);
                          const warehouse = selectedWHs.includes('All') || selectedWHs.length === 0 ? 'All' : selectedWHs.join(', ');
                          const commitment_type = (document.getElementById('cmType') as HTMLSelectElement)?.value || 'PALLETS';
                          const commitment_value = parseFloat((document.getElementById('cmValue') as HTMLInputElement)?.value || '0');
                          const conversion_factor = parseFloat((document.getElementById('cmFactor') as HTMLInputElement)?.value || '1');
                          const storage_type = (document.getElementById('cmStorageType') as HTMLSelectElement)?.value;
                          const notes = (document.getElementById('cmNotes') as HTMLInputElement)?.value || null;
                          if (!customer || selectedCustomers.length === 0) { alert('Please select at least one customer'); return; }
                          if (!commitment_value || commitment_value <= 0) { alert('Please enter a commitment value'); return; }
                          addCustMin({ customer, warehouse, commitment_type, commitment_value, conversion_factor, storage_type, notes });
                        }}
                        style={{ padding: '10px 28px', borderRadius: '8px', background: custMinLoading ? '#94a3b8' : '#0ea5e9', color: '#fff', border: 'none', fontSize: '14px', fontWeight: 600, cursor: custMinLoading ? 'wait' : 'pointer' }}
                      >
                        {custMinLoading ? 'Saving...' : 'Save Commitment'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Commitments Table */}
                {custMinRows.length > 0 && (
                  <table className="ed-table" style={{ fontSize: '12px' }}>
                    <thead className="ed-table-header">
                      <tr>
                        <th className="ed-table-cell" style={{ minWidth: '160px' }}>Customer</th>
                        <th className="ed-table-cell">Warehouse</th>
                        <th className="ed-table-cell">Type</th>
                        <th className="ed-table-cell ed-table-numeric">Value</th>
                        <th className="ed-table-cell ed-table-numeric">Factor</th>
                        <th className="ed-table-cell ed-table-numeric" style={{ background: '#f0f9ff' }}>Min PP</th>
                        <th className="ed-table-cell ed-table-numeric">Actual PP</th>
                        <th className="ed-table-cell ed-table-numeric" style={{ background: '#fffbeb' }}>Effective</th>
                        <th className="ed-table-cell ed-table-numeric" style={{ background: '#fef2f2' }}>Shortfall</th>
                        <th className="ed-table-cell">Storage</th>
                        <th className="ed-table-cell" style={{ width: '55px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {enriched.map((row, idx) => (
                        <tr key={idx} className="ed-table-row">
                          <td className="ed-table-cell" style={{ fontWeight: 600 }}>
                            {row.customer.includes(',') ? (
                              <div>
                                <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '4px', background: '#dbeafe', color: '#1d4ed8', fontSize: '9px', fontWeight: 700, marginBottom: '2px' }}>GROUP ({row.customer.split(',').length})</span>
                                <div style={{ fontSize: '11px', lineHeight: '1.4' }}>{row.customer.split(',').map(c => c.trim()).join(', ')}</div>
                              </div>
                            ) : row.customer}
                          </td>
                          <td className="ed-table-cell">
                            {row.warehouse === 'All' ? 'All Warehouses' : row.warehouse.includes(',') ? (
                              <div>
                                <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '4px', background: '#ede9fe', color: '#5b21b6', fontSize: '9px', fontWeight: 700, marginBottom: '2px' }}>{row.warehouse.split(',').length} WHs</span>
                                <div style={{ fontSize: '10px', lineHeight: '1.3' }}>{row.warehouse.split(',').map(w => getWarehouseDisplayName(w.trim())).join(', ')}</div>
                              </div>
                            ) : getWarehouseDisplayName(row.warehouse)}
                          </td>
                          <td className="ed-table-cell">
                            <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                              background: row.commitmentType === 'PALLETS' ? '#eef2ff' : row.commitmentType === 'SQFT' ? '#fef3c7' : row.commitmentType === 'MT' ? '#dcfce7' : row.commitmentType === 'BINS' ? '#e0f2fe' : row.commitmentType === 'KT' ? '#fce7f3' : '#fae8ff',
                              color: row.commitmentType === 'PALLETS' ? '#3730a3' : row.commitmentType === 'SQFT' ? '#92400e' : row.commitmentType === 'MT' ? '#166534' : row.commitmentType === 'BINS' ? '#0369a1' : row.commitmentType === 'KT' ? '#9d174d' : '#86198f',
                            }}>{row.cTypeInfo.unit}</span>
                          </td>
                          <td className="ed-table-cell ed-table-numeric">{formatNumber(row.commitmentValue)}</td>
                          <td className="ed-table-cell ed-table-numeric" style={{ color: '#6366f1', fontSize: '11px', fontWeight: 600 }}>{row.cTypeInfo.displayFactor || `x${row.conversionFactor}`}</td>
                          <td className="ed-table-cell ed-table-numeric" style={{ fontWeight: 700, color: '#0369a1' }}>{formatNumber(row.minPallets)}</td>
                          <td className="ed-table-cell ed-table-numeric">{formatNumber(row.actual)}</td>
                          <td className="ed-table-cell ed-table-numeric" style={{ fontWeight: 600, color: '#92400e' }}>{formatNumber(row.effective)}</td>
                          <td className="ed-table-cell ed-table-numeric" style={{ fontWeight: 600, color: row.shortfall > 0 ? '#dc2626' : '#16a34a' }}>
                            {row.shortfall > 0 ? `+${formatNumber(row.shortfall)}` : '0'}
                          </td>
                          <td className="ed-table-cell" style={{ fontSize: '11px' }}>{row.storageType || '—'}</td>
                          <td className="ed-table-cell" style={{ textAlign: 'center' }}>
                            <button onClick={() => row.id ? deleteCustMin(row.id) : null}
                              style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '3px 8px', color: '#dc2626', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>
                              Del
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr className="ed-table-row" style={{ background: '#f1f5f9', fontWeight: 700, borderTop: '2px solid #cbd5e1' }}>
                        <td className="ed-table-cell">TOTAL ({enriched.length})</td>
                        <td className="ed-table-cell" colSpan={4}></td>
                        <td className="ed-table-cell ed-table-numeric" style={{ color: '#0369a1' }}>{formatNumber(totalMin)}</td>
                        <td className="ed-table-cell ed-table-numeric">{formatNumber(totalActual)}</td>
                        <td className="ed-table-cell ed-table-numeric" style={{ color: '#92400e' }}>{formatNumber(totalEffective)}</td>
                        <td className="ed-table-cell ed-table-numeric" style={{ color: totalShortfall > 0 ? '#dc2626' : '#16a34a' }}>
                          {totalShortfall > 0 ? `+${formatNumber(totalShortfall)}` : '0'}
                        </td>
                        <td className="ed-table-cell" colSpan={2}></td>
                      </tr>
                    </tbody>
                  </table>
                )}

                {totalShortfall > 0 && (
                  <div style={{ marginTop: '14px', padding: '14px 20px', background: 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)', borderRadius: '12px', border: '1px solid #fecaca', fontSize: '13px', color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><strong>Shortfall Impact:</strong> {formatNumber(totalShortfall)} pallet positions reserved by minimum commitments but currently unoccupied — counted as used capacity.</span>
                    <span style={{ fontWeight: 800, fontSize: '15px', background: '#dc2626', color: '#fff', padding: '4px 14px', borderRadius: '8px' }}>+{formatNumber(totalShortfall)} PP</span>
                  </div>
                )}

                {custMinRows.length === 0 && (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', border: '2px dashed #e2e8f0', borderRadius: '16px', background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>📋</div>
                    <div style={{ fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>No customer commitments yet</div>
                    Click <strong style={{ color: '#6366f1' }}>"+ New Commitment"</strong> to add a customer contract with their minimum pallet requirement.
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        </div>
        );
      })()}

      {/* Drill-down: Bin-level detail for a selected warehouse */}
      {drillDownWh && (() => {
        // Get warehouse summary from dynamicCapacityStats
        const whRow = dynamicCapacityStats.warehouseRows.find((r: any) => r.warehouse === drillDownWh);

        const whBins = rawWarehouseData
          .filter((item: any) => {
            if (item.SOURCE === 'MANUAL') return false;
            return (item.WAREHOUSE || '').trim() === drillDownWh;
          })
          .map((item: any) => {
            const seg = item.TOTAL_NO_OF_SEGMENTS || 0;
            const free = item.FREE_SEGMENTS || 0;
            const lvl = item.NUMBER_OF_LEVELS || 0;
            const blocked = String(item.LPFSTA || '').trim() === '2' || item.OCCUPANCY_STATUS === 'TEMP_BLOCKED';
            const isFloor = seg === 0;
            const isVirtual = seg >= 99;
            const cap = calcCap(seg, lvl);
            const used = blocked ? cap : Math.max(0, seg - free);
            let type = 'Rack SM';
            if (blocked) type = 'Blocked';
            else if (isFloor) type = 'Floor';
            else if (isVirtual) type = 'Virtual';
            else if (seg > THRESHOLD) type = 'Rack LG (BULK)';
            return {
              area: item.STORAGE_AREA || '-',
              level: lvl,
              shelfSize: item.SHELF_SIZE || (isFloor ? 'FLOOR' : '-'),
              segments: seg,
              free,
              cap,
              used,
              freePal: Math.max(0, cap - used),
              type,
              status: blocked ? 'BLOCKED' : free === 0 ? 'FULL' : free === seg ? 'EMPTY' : 'PARTIAL',
            };
          })
          .sort((a: any, b: any) => a.area.localeCompare(b.area) || b.segments - a.segments);

        const exportToExcel = () => {
          const rows = whBins.map((b: any) => ({
            'Storage Area': b.area, 'Level': b.level, 'Shelf Size': b.shelfSize,
            'Type': b.type, 'Segments': b.segments, 'Free Seg': b.free,
            'Pallet Cap': b.cap, 'Used Pallets': b.used, 'Free Pallets': b.freePal, 'Status': b.status,
          }));
          // Add summary row at top
          if (whRow) {
            rows.unshift({
              'Storage Area': `SUMMARY: ${getWarehouseDisplayName(drillDownWh)}`,
              'Level': 0, 'Shelf Size': '', 'Type': '',
              'Segments': 0, 'Free Seg': 0,
              'Pallet Cap': whRow.tCap, 'Used Pallets': whRow.tUsed, 'Free Pallets': whRow.tFree,
              'Status': `${whRow.tCap > 0 ? Math.round((whRow.tUsed / whRow.tCap) * 100) : 0}%`,
            });
          }
          const ws = XLSX.utils.json_to_sheet(rows);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, drillDownWh);
          XLSX.writeFile(wb, `${drillDownWh}_Dynamic_Capacity.xlsx`);
        };

        const whPct = whRow && whRow.tCap > 0 ? Math.round((whRow.tUsed / whRow.tCap) * 100 * 10) / 10 : 0;

        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: '16px', width: '95%', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button onClick={() => setDrillDownWh(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                  ← Back
                </button>
                <h2 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>{getWarehouseDisplayName(drillDownWh)}</h2>
                <span style={{ fontSize: '13px', color: '#94a3b8' }}>{whBins.length} bins</span>
                <button onClick={exportToExcel} style={{ marginLeft: 'auto', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                  Export Excel
                </button>
              </div>

              {/* Warehouse Summary KPIs */}
              {whRow && (
                <div style={{ padding: '14px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Total Cap</span>
                    <span style={{ fontSize: '16px', fontWeight: 700, color: '#0369a1' }}>{formatNumber(whRow.tCap)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Total Used</span>
                    <span style={{ fontSize: '16px', fontWeight: 700, color: '#92400e' }}>{formatNumber(whRow.tUsed)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Free</span>
                    <span style={{ fontSize: '16px', fontWeight: 700, color: '#16a34a' }}>{formatNumber(whRow.tFree)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: whPct > 85 ? '#fef2f2' : '#fff', borderRadius: '8px', border: `1px solid ${whPct > 85 ? '#fecaca' : '#e2e8f0'}` }}>
                    <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Util</span>
                    <span style={{ fontSize: '16px', fontWeight: 700, color: whPct > 85 ? '#dc2626' : whPct > 70 ? '#d97706' : '#16a34a' }}>{whPct}%</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '11px', color: '#64748b' }}>
                    Rack: {formatNumber(whRow.rackUsed)} · Blocked: {formatNumber(whRow.blockedCap)} · Virtual: {formatNumber(whRow.virtualUsed)} · Floor: {formatNumber(whRow.floorPallets)} · Manual: {formatNumber(whRow.manualPallets)}
                  </div>
                </div>
              )}
              {/* Table */}
              <div style={{ overflow: 'auto', flex: 1, padding: '0' }}>
                <table className="ed-table" style={{ fontSize: '12px', margin: 0 }}>
                  <thead className="ed-table-header" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th className="ed-table-cell">Storage Area</th>
                      <th className="ed-table-cell ed-table-numeric">Level</th>
                      <th className="ed-table-cell">Shelf Size</th>
                      <th className="ed-table-cell">Type</th>
                      <th className="ed-table-cell ed-table-numeric">Segments</th>
                      <th className="ed-table-cell ed-table-numeric">Free Seg</th>
                      <th className="ed-table-cell ed-table-numeric">Pallet Cap</th>
                      <th className="ed-table-cell ed-table-numeric">Used</th>
                      <th className="ed-table-cell ed-table-numeric">Free</th>
                      <th className="ed-table-cell">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {whBins.map((bin: any, idx: number) => (
                      <tr key={idx} className="ed-table-row">
                        <td className="ed-table-cell" style={{ fontWeight: 500 }}>{bin.area}</td>
                        <td className="ed-table-cell ed-table-numeric">{bin.level}</td>
                        <td className="ed-table-cell">{bin.shelfSize}</td>
                        <td className="ed-table-cell">
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                            background: bin.type === 'Rack LG (BULK)' ? '#fef3c7' : bin.type === 'Rack SM' ? '#eef2ff' : bin.type === 'Blocked' ? '#fee2e2' : bin.type === 'Floor' ? '#dcfce7' : '#e0f2fe',
                            color: bin.type === 'Rack LG (BULK)' ? '#92400e' : bin.type === 'Rack SM' ? '#3730a3' : bin.type === 'Blocked' ? '#991b1b' : bin.type === 'Floor' ? '#166534' : '#0369a1',
                          }}>{bin.type}</span>
                        </td>
                        <td className="ed-table-cell ed-table-numeric">{bin.segments}</td>
                        <td className="ed-table-cell ed-table-numeric">{bin.free}</td>
                        <td className="ed-table-cell ed-table-numeric" style={{ fontWeight: 600 }}>{bin.cap}</td>
                        <td className="ed-table-cell ed-table-numeric" style={{ fontWeight: 600, color: '#92400e' }}>{bin.used}</td>
                        <td className="ed-table-cell ed-table-numeric" style={{ color: '#16a34a' }}>{bin.freePal}</td>
                        <td className="ed-table-cell">
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                            background: bin.status === 'FULL' ? '#fee2e2' : bin.status === 'EMPTY' ? '#dcfce7' : bin.status === 'BLOCKED' ? '#fef3c7' : '#f0f9ff',
                            color: bin.status === 'FULL' ? '#991b1b' : bin.status === 'EMPTY' ? '#166534' : bin.status === 'BLOCKED' ? '#92400e' : '#0369a1',
                          }}>{bin.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// Sub-components

const KPICard: React.FC<{
  icon: string;
  label: string;
  value: string;
  subtitle: string;
  variant: string;
}> = ({ icon, label, value, subtitle, variant }) => (
  <div className={`ed-kpi-card ed-kpi-card-${variant}`}>
    <div className="ed-kpi-header">
      <div className="ed-kpi-icon-bg">{icon}</div>
    </div>
    <div className="ed-kpi-content">
      <div className="ed-kpi-label">{label}</div>
      <div className="ed-kpi-value">{value}</div>
      <div className="ed-kpi-sub">{subtitle}</div>
    </div>
  </div>
);

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="ed-stat-card">
    <div className="ed-stat-label">{label}</div>
    <div className="ed-stat-value">{value}</div>
  </div>
);

const StorageTypeCard: React.FC<{
  title: string;
  value: number;
  color: string;
  formatNumber: (n: number) => string;
  subtext?: string;
  onClick?: () => void;
}> = ({ title, value, color, formatNumber, subtext, onClick }) => (
  <div
    className="ed-storage-type-card"
    style={{ borderLeftColor: color, cursor: onClick ? 'pointer' : 'default' }}
    onClick={onClick}
  >
    <div className="ed-storage-type-dot" style={{ backgroundColor: color }} />
    <div className="ed-storage-type-title">{title}</div>
    <div className="ed-storage-type-value" style={{ color }}>
      {formatNumber(value)}
    </div>
    {subtext && <div className="ed-storage-type-subtext">{subtext}</div>}
    {onClick && (
      <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}>
        <span style={{ fontSize: '12px' }}>&#8594;</span> View Details
      </div>
    )}
  </div>
);

const ProgressBar: React.FC<{ used: number; total: number; label?: string; showLabel?: boolean }> = ({
  used,
  total,
  label,
  showLabel = false,
}) => {
  const percent = total > 0 ? Math.round((used / total) * 100) : 0;
  const displayLabel = label || `${percent}%`;
  const fillColor = percent > 80 ? '#ef4444' : percent > 50 ? '#f59e0b' : '#10b981';

  return (
    <div className="ed-progress-container">
      <div className="ed-progress-bar">
        <div
          className="ed-progress-fill"
          style={{
            width: `${percent}%`,
            backgroundColor: fillColor,
          }}
        />
      </div>
      {showLabel && <span className="ed-progress-label">{displayLabel}</span>}
    </div>
  );
};

const StorageTypeBadge: React.FC<{ type: string }> = ({ type }) => {
  const displayType = type === 'IBM' ? 'Locations' : type || 'Unknown';
  const badgeClass = `ed-badge ed-badge-${displayType.toLowerCase()}`;
  return <span className={badgeClass}>{displayType}</span>;
};

const EdTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  label?: any;
  getWarehouseDisplayName: (code: string) => string;
}> = ({ active, payload, label, getWarehouseDisplayName }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="ed-chart-tooltip">
      <div className="ed-tooltip-title">{getWarehouseDisplayName(String(label))}</div>
      {payload.map((entry: any, i: number) => (
        <div key={i} className="ed-tooltip-row">
          <span className="ed-tooltip-dot" style={{ background: entry.color }} />
          <span className="ed-tooltip-name">{entry.name}</span>
          <span className="ed-tooltip-val">
            {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export default ExecutiveDashboard;
